import { Chart, registerables } from 'chart.js';
import tzlookup from 'tz-lookup';
import store, { AppState } from '../state';
import {
  ForecastBinaryService,
  DecodedPointForecast,
  DecodedValleyFog,
  getRawHourOffsetsForModel
} from './ForecastBinaryService';
import { getGenericScoreAt } from './PoiDataService';
import { getTileUrl, cleanHour } from '../config';
import ManifestTimelineHelper from './ManifestTimelineHelper';
import { getGoldenWindowRating } from '../utils/goldenWindowRating';
import themeService from './ThemeService';
import {
  TierAccessService,
  AccessTier,
  getRequiredTierForLayer,
  hasTierAccess,
} from './TierAccessService';

Chart.register(...registerables);

export interface TimeTickInfo {
  index: number;
  timestamp: number;
  isMidnight: boolean;
  isNoon: boolean;
  label: string;
}

interface ChartEntry {
  chart: Chart;
  poiKey: string;
  model: string;
  units: string;
  activeIdx: number;
  timestamps: number[];
  horizonDays: number;
  startDayKey: string;
  optionsSubKey?: string;
  isDaily?: boolean;
  themeId: string;
  targetProduct?: string;
  dateStrings?: string[];
  evMorningKey?: string;
  evEveningKey?: string;
  refLinesEnabled?: boolean;
  timeTicksMap?: Map<number, TimeTickInfo>;
}

export class LegendTrendChartService {
  private static charts = new Map<string, ChartEntry>();
  private static pendingRequests = new Map<string, number>();
  private static solarScoresCache = new Map<string, { morningScores: number[]; eveningScores: number[] }>();
  private static currentTimeTicksMap: Map<number, TimeTickInfo> | null = null;

  public static clearCaches(): void {
    this.solarScoresCache.clear();
  }

  public static getSupportedLayers(): string[] {
    return [
      'hrtemp',
      'sunrise',
      'alpenglow',
      'goldenhour',
      'lightning',
      'thunderstorm',
      'clouds',
      'winds',
      'precip',
      'relative_humidity',
      'dewpoint',
      'optics_dew_risk',
      'seeing',
      'turbulence',
      'freezing_level',
      'windchill',
      'smoke',
      'dust',
      'aqi',
      'darkskies',
      'sun_intensity',
      'valley_fog',
      'cloud_inversion',
      'cloud_base',
      'lenticular'
    ];
  }

  private static normalizeLayerKey(layerKey: string): string {
    if (layerKey === 'precip_forecast') return 'precip';
    if (layerKey === 'dust_forecast') return 'dust';
    if (layerKey === 'aviation_turbulence' || layerKey === 'turbulence_forecast') return 'turbulence';
    return layerKey;
  }

  public static isSupportedLayer(layerKey: string): boolean {
    const canonKey = this.normalizeLayerKey(layerKey);
    return this.getSupportedLayers().includes(canonKey);
  }

  public static getChartEntry(layerKey: string): ChartEntry | undefined {
    const canonKey = this.normalizeLayerKey(layerKey);
    return this.charts.get(canonKey) || this.charts.get(layerKey);
  }

  public static setTimeFromChartClick(layerKey: string, pixelX: number, pixelY: number): boolean {
    const canonKey = this.normalizeLayerKey(layerKey);
    const entry = this.charts.get(canonKey) || this.charts.get(layerKey);
    if (!entry || !entry.chart || !entry.chart.chartArea || !entry.chart.scales.x) {
      return false;
    }

    const chart = entry.chart;
    const chartArea = chart.chartArea;

    // Check vertical bounds (allow margin above/below data area)
    if (pixelY < chartArea.top - 12 || pixelY > chartArea.bottom + 25) {
      return false;
    }

    const clampedX = Math.max(chartArea.left, Math.min(pixelX, chartArea.right));
    const state = store.getState();
    const timelineRange = state.timelineRange;
    if (!timelineRange || timelineRange.length === 0) {
      return false;
    }

    let targetDate: Date | null = null;

    if (entry.isDaily) {
      const rawVal = chart.scales.x.getValueForPixel(clampedX);
      const numDays = entry.timestamps.length;
      const dayIdx = Math.max(0, Math.min(numDays - 1, Math.round(rawVal ?? 0)));

      if (entry.dateStrings && entry.evMorningKey && entry.evEveningKey) {
        const targetDateStr = entry.dateStrings[dayIdx];
        const centerPixel = chart.scales.x.getPixelForValue(dayIdx);
        const isMorning = clampedX <= centerPixel;
        const targetEvent = isMorning ? entry.evMorningKey : entry.evEveningKey;

        const match = timelineRange.find(t => {
          const hClean = cleanHour(t.hour);
          return t.date === targetDateStr && (hClean === targetEvent || t.hour === targetEvent);
        });

        if (match) {
          targetDate = ManifestTimelineHelper.getStepDate(match);
        } else {
          const approxMs = isMorning
            ? Date.parse(`${targetDateStr}T13:00:00Z`)
            : (Date.parse(`${targetDateStr}T00:00:00Z`) + 26 * 3600 * 1000);
          let bestStep = timelineRange[0];
          let minDiff = Infinity;
          for (const step of timelineRange) {
            const stepD = ManifestTimelineHelper.getStepDate(step);
            const diff = Math.abs(stepD.getTime() - approxMs);
            if (diff < minDiff) {
              minDiff = diff;
              bestStep = step;
            }
          }
          targetDate = ManifestTimelineHelper.getStepDate(bestStep);
        }
      } else {
        const targetDateStr = entry.dateStrings?.[dayIdx];
        if (targetDateStr) {
          const match = timelineRange.find(t => t.date === targetDateStr);
          if (match) {
            targetDate = ManifestTimelineHelper.getStepDate(match);
          }
        }
        if (!targetDate && entry.timestamps[dayIdx]) {
          const targetMs = entry.timestamps[dayIdx];
          let bestStep = timelineRange[0];
          let minDiff = Infinity;
          for (const step of timelineRange) {
            const stepD = ManifestTimelineHelper.getStepDate(step);
            const diff = Math.abs(stepD.getTime() - targetMs);
            if (diff < minDiff) {
              minDiff = diff;
              bestStep = step;
            }
          }
          targetDate = ManifestTimelineHelper.getStepDate(bestStep);
        }
      }
    } else {
      const numPoints = entry.timestamps.length;
      if (numPoints >= 2) {
        const startMs = entry.timestamps[0];
        const endMs = entry.timestamps[numPoints - 1];
        const x0 = chart.scales.x.getPixelForValue(0);
        const xEnd = chart.scales.x.getPixelForValue(numPoints - 1);
        const spanX = xEnd - x0;
        const ratio = spanX > 0 ? (clampedX - x0) / spanX : 0;
        const targetMs = startMs + Math.max(0, Math.min(1, ratio)) * (endMs - startMs);

        let bestStep = timelineRange[0];
        let minDiff = Infinity;
        for (const step of timelineRange) {
          const stepD = ManifestTimelineHelper.getStepDate(step);
          const diff = Math.abs(stepD.getTime() - targetMs);
          if (diff < minDiff) {
            minDiff = diff;
            bestStep = step;
          }
        }
        targetDate = ManifestTimelineHelper.getStepDate(bestStep);
      }
    }

    if (targetDate) {
      if (!state.currentTimeStep || state.currentTimeStep.getTime() !== targetDate.getTime()) {
        store.update({
          currentTimeStep: targetDate,
          forecastAnimationStartTime: null
        });
        return true;
      }
    }
    return false;
  }

  public static attachInteractionListeners(canvas: HTMLCanvasElement, layerKey: string): void {
    if ((canvas as any)._trendClickAttached) return;
    (canvas as any)._trendClickAttached = true;
    canvas.style.cursor = 'pointer';

    canvas.addEventListener('click', (e: MouseEvent) => {
      if (e.button !== 0) return;
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      LegendTrendChartService.setTimeFromChartClick(layerKey, clickX, clickY);
    });
  }

  public static destroyChart(layerKey: string): void {
    const nextReq = (this.pendingRequests.get(layerKey) || 0) + 1;
    this.pendingRequests.set(layerKey, nextReq);

    const entry = this.charts.get(layerKey);
    if (entry) {
      try {
        entry.chart.destroy();
      } catch (_e) {
        // ignore
      }
      this.charts.delete(layerKey);
    }
    const canvas = document.getElementById(`legend-${layerKey}-chart`) as HTMLCanvasElement;
    if (canvas) {
      try {
        const existing = Chart.getChart(canvas);
        if (existing) {
          existing.destroy();
        }
      } catch (_e) {
        // ignore
      }
    }
  }

  public static destroyAll(): void {
    for (const key of this.getSupportedLayers()) {
      const nextReq = (this.pendingRequests.get(key) || 0) + 1;
      this.pendingRequests.set(key, nextReq);
    }
    for (const entry of this.charts.values()) {
      try {
        entry.chart.destroy();
      } catch (_e) {
        // ignore
      }
    }
    this.charts.clear();
    for (const key of this.getSupportedLayers()) {
      const canvas = document.getElementById(`legend-${key}-chart`) as HTMLCanvasElement;
      if (canvas) {
        try {
          const existing = Chart.getChart(canvas);
          if (existing) {
            existing.destroy();
          }
        } catch (_e) {
          // ignore
        }
      }
    }
  }

  public static renderChartContainerHtml(layerKey: string, state: AppState, inDrawer: boolean = false): string {
    if (inDrawer || (state as any)._isDrawerRendering) return '';
    const poi = state.selectedPoi;
    if (!poi) return '';

    if (layerKey === 'sunrise' || layerKey === 'alpenglow' || layerKey === 'goldenhour') {
      const targetProd = (layerKey === 'alpenglow' && state.alpenglowMode === 'goldenhour') ? 'goldenhour' : layerKey;
      const activeModel = String(state.selectedModels?.[targetProd] || state.selectedModels?.[layerKey] || '').toLowerCase();
      if (activeModel === 'gw_nowcast') {
        return '';
      }
    }

    const isImperial = state.units === 'imperial';
    const elevDisplay = isImperial ? `${Math.round(poi.elev)}'` : `${Math.round(poi.elev * 0.3048)}m`;
    let model = (
      layerKey === 'valley_fog' ? (state.selectedModels['valley_fog'] || 'nbm') :
      layerKey === 'aqi' ? (state.selectedModels['aqi'] || 'gefs') :
      layerKey === 'smoke' ? (state.selectedModels['smoke'] || 'hrrr') :
      (layerKey === 'dust' || layerKey === 'dust_forecast') ? (state.selectedModels['dust'] || 'gefs_dust') :
      layerKey === 'sun_intensity' ? (state.selectedModels['sun_intensity'] || state.sunIntensityModel || 'rrfs') :
      layerKey === 'thunderstorm' ? (state.selectedModels['thunderstorm'] || 'hrrr') :
      layerKey === 'turbulence' ? (state.selectedModels['turbulence'] || state.selectedModels['aviation_turbulence'] || 'gfs') :
      layerKey === 'sunrise' ? (state.selectedModels['sunrise'] || 'gw_v2_blend_50') :
      (layerKey === 'alpenglow' || layerKey === 'goldenhour') ? (state.selectedModels[layerKey] || (state.alpenglowMode === 'goldenhour' ? state.selectedModels['goldenhour'] : null) || 'gw_v2_blend_50') :
      (state.selectedModels[layerKey] || 'blend')
    ).toUpperCase();
    if (model === 'GW_V2_BLEND_50' || model === 'BLEND_50' || model === 'SKYSK_HRES_TOPO') {
      model = 'SKYSKOUT HRES';
    } else if (model.startsWith('GW_V2_')) {
      model = model.replace(/^GW_V2_/, '');
    }

    const titles: Record<string, string> = {
      hrtemp: 'Temp Trend',
      sunrise: 'Sunrise & Sunset Vibrancy',
      goldenhour: 'Golden Hour Quality',
      alpenglow: 'Alpenglow Quality',
      lightning: 'Lightning Hazard',
      thunderstorm: 'Thunderstorm Prob',
      clouds: 'Cloud Cover',
      winds: 'Winds & Gusts',
      precip: 'Precipitation',
      relative_humidity: 'Relative Humidity',
      dewpoint: 'Dew Point',
      optics_dew_risk: 'Optics Dew Risk',
      seeing: 'Astronomical Seeing',
      turbulence: 'Aviation Turbulence (EDR)',
      freezing_level: 'Freezing Level',
      windchill: 'Wind Chill',
      smoke: 'Surface Smoke',
      dust: 'Surface Dust',
      aqi: model === 'RAQDPS' ? 'Canada AQHI' : 'Air Quality Index',
      darkskies: 'Dark Skies Quality',
      sun_intensity: 'Sun Intensity',
      valley_fog: 'Valley Fog Probability',
      cloud_inversion: 'Sea of Clouds',
      cloud_base: 'Cloud Base Altitude'
    };

    let chartTitle = titles[layerKey] || 'Forecast Trend';
    if (layerKey === 'precip') {
      const pMode = state.precipViewMode || 'hourly';
      if (pMode === '24h_snow') {
        model = (state.selectedModels['snow_24h'] || state.selectedModels['precip'] || 'nbm').toUpperCase();
        chartTitle = 'Daily Snow';
      } else if (pMode === '24h_rain' || pMode === '24h') {
        model = (state.selectedModels['precip_24h'] || state.selectedModels['precip'] || 'nbm').toUpperCase();
        chartTitle = 'Daily Precip';
      } else if (pMode === 'prob') {
        model = (state.selectedModels['precip_prob'] || state.selectedModels['precip'] || 'nbm').toUpperCase();
        chartTitle = 'Precip Probability';
      } else {
        model = (state.selectedModels['precip'] || 'nbm').toUpperCase();
        chartTitle = 'Precip Rate';
      }
    }

    const isEnabled = state.showLegendTrendCharts !== false;
    const horizonDays = state.timelineHorizon === 10 ? 10 : 5;

    const locationHeaderHtml = `
      <div class="legend-poi-location-details" style="display: flex; flex-direction: column; gap: 3px; margin-top: 4px; padding: 6px 8px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.06); border-radius: 6px; font-size: 0.72rem;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #38bdf8; font-weight: 600;">Location:</span>
          <span style="font-family: monospace; font-size: 0.7rem; color: var(--text-primary);">${poi.lat.toFixed(4)}°, ${poi.lng.toFixed(4)}°</span>
        </div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: var(--text-muted);">Elevation:</span>
          <span style="font-weight: 600; color: var(--text-primary);">${elevDisplay}</span>
        </div>
      </div>
    `;

    if (!isEnabled) {
      return `
        ${locationHeaderHtml}
        <div class="legend-${layerKey}-chart-container" style="display: flex; justify-content: space-between; align-items: center; margin-top: 4px; background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.06); padding: 6px 8px; border-radius: 6px;">
          <span style="font-size: 0.7rem; font-weight: 700; color: var(--text-primary); text-transform: uppercase; letter-spacing: 0.5px;">${model} ${chartTitle}</span>
          <button class="legend-chart-toggle-btn" style="background: rgba(56,189,248,0.12); border: 1px solid rgba(56,189,248,0.3); color: #38bdf8; font-size: 0.65rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; cursor: pointer; transition: all 0.15s ease;">Enable Chart</button>
        </div>
      `;
    }

    return `
      ${locationHeaderHtml}
      <div class="legend-${layerKey}-chart-container" style="display: flex; flex-direction: column; gap: 4px; margin-top: 4px; background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.06); padding: 8px 6px; border-radius: 6px;">
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 0 4px; gap: 6px;">
          <span style="font-size: 0.7rem; font-weight: 700; color: var(--text-primary); text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${model} ${chartTitle} (${horizonDays}D)</span>
          <div style="display: flex; align-items: center; gap: 6px; flex-shrink: 0;">
            <button class="legend-chart-toggle-btn" style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: var(--text-muted); font-size: 0.65rem; padding: 2px 6px; border-radius: 4px; cursor: pointer; transition: all 0.15s ease;">Disable</button>
          </div>
        </div>
        <div style="position: relative; width: 100%; height: 110px;">
          <div id="legend-${layerKey}-loading" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; color: var(--text-muted); background: rgba(0,0,0,0.4); border-radius: 4px; z-index: 2;">
            Loading forecast trend...
          </div>
          <canvas id="legend-${layerKey}-chart" style="width: 100%; height: 100%;"></canvas>
        </div>
      </div>
    `;
  }

  public static async updateCharts(state: AppState): Promise<void> {
    const poi = state.selectedPoi;
    const manifest = state.manifest;
    if (!poi || !manifest || state.showLegendTrendCharts === false) {
      this.destroyAll();
      return;
    }

    const poiKey = `${poi.lat.toFixed(4)}_${poi.lng.toFixed(4)}`;
    const units = state.units || 'imperial';
    const tz = tzlookup(poi.lat, poi.lng) || state.userTimezone || 'UTC';
    const horizonDays = state.timelineHorizon === 10 ? 10 : 5;

    const now = new Date();
    // Anchor to top of current UTC hour (matching ManifestTimelineHelper.generateMasterTimelineRange)
    const nowRoundedMs = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      0, 0, 0
    );
    // Render starting at -8H from "NOW", matching the start of the master date/time slider
    const startMs = nowRoundedMs - 8 * 3600 * 1000;
    const startDayKey = new Date(startMs).toISOString().split(':')[0];

    const currentTheme = typeof document !== 'undefined'
      ? (document.documentElement.getAttribute('data-theme') || 'dark')
      : 'dark';

    const activeLayers = state.activeLegendOrder || [];

    for (const layerKey of this.getSupportedLayers()) {
      const canvas = document.getElementById(`legend-${layerKey}-chart`) as HTMLCanvasElement;
      const isLayerActiveOrDocked = activeLayers.includes(layerKey) ||
                                    (layerKey === 'turbulence' && activeLayers.includes('aviation_turbulence')) ||
                                    layerKey === state.lastActivatedProduct ||
                                    (layerKey === 'turbulence' && state.lastActivatedProduct === 'aviation_turbulence') ||
                                    layerKey === state.primaryProduct ||
                                    (layerKey === 'turbulence' && state.primaryProduct === 'aviation_turbulence') ||
                                    (canvas && canvas.closest('#timeline-trend-dock') !== null);
      if (!canvas || !isLayerActiveOrDocked) {
        this.destroyChart(layerKey);
        continue;
      }

      let model = 'blend';
      if (layerKey === 'sunrise') {
        model = (state.selectedModels['sunrise'] || 'gw_v2_blend_50').toLowerCase();
      } else if (layerKey === 'alpenglow') {
        if (state.alpenglowMode === 'goldenhour') {
          model = (state.selectedModels['goldenhour'] || state.selectedModels['alpenglow'] || 'gw_v2_blend_50').toLowerCase();
        } else {
          model = (state.selectedModels['alpenglow'] || 'gw_v2_blend_50').toLowerCase();
        }
      } else if (layerKey === 'goldenhour') {
        model = (state.selectedModels['goldenhour'] || state.selectedModels['alpenglow'] || 'gw_v2_blend_50').toLowerCase();
      } else if (layerKey === 'precip') {
        const pMode = state.precipViewMode || 'hourly';
        if (pMode === '24h_snow') {
          model = (state.selectedModels['snow_24h'] || state.selectedModels['precip'] || 'nbm').toLowerCase();
        } else if (pMode === '24h_rain' || pMode === '24h') {
          model = (state.selectedModels['precip_24h'] || state.selectedModels['precip'] || 'nbm').toLowerCase();
        } else if (pMode === 'prob') {
          model = (state.selectedModels['precip_prob'] || state.selectedModels['precip'] || 'nbm').toLowerCase();
        } else {
          model = (state.selectedModels['precip'] || 'nbm').toLowerCase();
        }
      } else if (layerKey === 'valley_fog') {
        model = (state.selectedModels['valley_fog'] || 'nbm').toLowerCase();
      } else if (layerKey === 'cloud_inversion' || layerKey === 'cloud_base') {
        model = (state.selectedModels[layerKey] || 'nbm').toLowerCase();
      } else if (layerKey === 'aqi') {
        model = (state.selectedModels['aqi'] || 'gefs').toLowerCase();
      } else if (layerKey === 'smoke') {
        model = (state.selectedModels['smoke'] || 'hrrr').toLowerCase();
      } else if (layerKey === 'dust' || layerKey === 'dust_forecast') {
        model = (state.selectedModels['dust'] || 'gefs_dust').toLowerCase();
      } else if (layerKey === 'sun_intensity') {
        model = (state.selectedModels['sun_intensity'] || state.sunIntensityModel || 'rrfs').toLowerCase();
      } else if (layerKey === 'thunderstorm') {
        model = (state.selectedModels['thunderstorm'] || 'hrrr').toLowerCase();
      } else if (layerKey === 'turbulence') {
        const rawM = (state.selectedModels['turbulence'] || state.selectedModels['aviation_turbulence'] || 'gfs').toLowerCase();
        const availPtModels: string[] = manifest?.forecast_point_tiles?.available_models || [];
        model = (rawM === 'dafs' || rawM === 'hgefs' || (availPtModels.length > 0 && !availPtModels.includes(rawM) && rawM !== 'blend'))
          ? 'gfs'
          : rawM;
      } else {
        model = (state.selectedModels[layerKey] || 'blend').toLowerCase();
      }

      // Sub-option key for layers that change structure with UI controls (e.g. clouds breakdown vs total, precip modes, goldenhour vs alpenglow)
      const subKey = layerKey === 'clouds' ? (state.cloudLevels || ['total']).sort().join(',') :
                     layerKey === 'precip' ? (state.precipViewMode || 'hourly') :
                     layerKey === 'aqi' ? (model === 'raqdps' ? 'aqhi' : (state.aqiMode || 'aqi')) :
                     layerKey === 'alpenglow' ? (state.alpenglowMode || 'goldenhour') : '';

      const existing = this.charts.get(layerKey);
      if (
        existing &&
        existing.chart.canvas === canvas &&
        existing.poiKey === poiKey &&
        existing.model === model &&
        existing.units === units &&
        existing.horizonDays === horizonDays &&
        existing.startDayKey === startDayKey &&
        existing.optionsSubKey === subKey &&
        existing.themeId === currentTheme
      ) {
        // Fast path: Only timeline cursor update
        const refLinesState = state.trendRefLinesEnabled !== false;
        const refLinesChanged = existing.refLinesEnabled !== refLinesState;
        const newActiveIdx = this.findActiveIndex(existing.timestamps, state.currentTimeStep, existing.isDaily);
        if (newActiveIdx !== existing.activeIdx || existing.isDaily || refLinesChanged) {
          existing.activeIdx = newActiveIdx;
          existing.refLinesEnabled = refLinesState;
          if (existing.isDaily) {
            let activeDay = -1;
            let isMorn = true;
            if (existing.dateStrings && existing.evMorningKey && existing.evEveningKey) {
              const res = this.resolveActiveDailyForecast(
                existing.targetProduct || layerKey,
                existing.model,
                state,
                existing.dateStrings,
                existing.timestamps,
                existing.horizonDays,
                tz,
                existing.evMorningKey,
                existing.evEveningKey
              );
              activeDay = res.activeDayIdx;
              isMorn = res.isMorning;
            } else {
              activeDay = Math.min(existing.horizonDays - 1, Math.max(0, Math.floor(newActiveIdx + 0.5)));
            }

            existing.chart.data.datasets.forEach(ds => {
              if (ds.type === 'bar' && (ds as any)._updateColors) {
                (ds as any)._updateColors(activeDay, isMorn);
              }
            });
          }
          existing.chart.update('none');
        }
        continue;
      }

      // Rebuild chart
      this.destroyChart(layerKey);
      const reqId = this.pendingRequests.get(layerKey)!;

      const loadingEl = document.getElementById(`legend-${layerKey}-loading`);
      if (loadingEl) {
        loadingEl.style.display = 'flex';
        loadingEl.style.color = 'var(--text-muted)';
        loadingEl.innerText = 'Loading forecast trend...';
      }
      canvas.style.display = 'none';

      const userTier = TierAccessService.getUserAccessTier(state);
      const reqPointTier = getRequiredTierForLayer(
        layerKey === 'valley_fog' ? 'valley_fog_point_tiles' : 'forecast_point_tiles'
      );
      if (
        !hasTierAccess(userTier, reqPointTier) &&
        ForecastBinaryService.lastAccessDenial &&
        Date.now() - ForecastBinaryService.lastAccessDenial.timestamp < 60_000
      ) {
        this.showEmptyState(canvas, loadingEl, model, reqPointTier);
        continue;
      }

      try {
        if (layerKey === 'lenticular') {
          if (loadingEl) {
            loadingEl.style.display = 'flex';
            loadingEl.style.flexDirection = 'column';
            loadingEl.style.alignItems = 'center';
            loadingEl.style.justifyContent = 'center';
            loadingEl.style.gap = '4px';
            loadingEl.style.padding = '8px';
            loadingEl.style.textAlign = 'center';
            loadingEl.style.color = 'var(--text-primary)';
            loadingEl.innerHTML = `<span style="font-weight: 600; font-size: 11px;">Lenticular Cloud Index (${model.toUpperCase()})</span><span style="font-size: 9px; color: var(--text-muted);">Spatial orographic wave model rendered on map canvas. Select model above to change forecast source.</span>`;
          }
          canvas.style.display = 'none';
          continue;
        } else if (layerKey === 'valley_fog') {
          const fogData = await ForecastBinaryService.getValleyFogForPoint(poi.lat, poi.lng, manifest, model);
          if (this.pendingRequests.get(layerKey) !== reqId || !canvas.isConnected) {
            return;
          }
          if (!fogData || fogData.numHours === 0 || !fogData.fogProbability || fogData.fogProbability.length === 0) {
            this.showEmptyState(canvas, loadingEl, model);
            continue;
          }
          this.buildValleyFogChart(layerKey, canvas, loadingEl, fogData, poiKey, model, units, tz, state, horizonDays, startMs, startDayKey, reqId, currentTheme);
        } else if (layerKey === 'sunrise' || layerKey === 'alpenglow' || layerKey === 'goldenhour') {
          await this.buildDailyDiscreteChart(
            layerKey,
            canvas,
            loadingEl,
            poi,
            poiKey,
            model,
            units,
            tz,
            state,
            subKey,
            horizonDays,
            startMs,
            startDayKey,
            reqId,
            currentTheme
          );
        } else {
          const forecast = await ForecastBinaryService.getForecastForPoint(poi.lat, poi.lng, manifest, model);
          if (this.pendingRequests.get(layerKey) !== reqId || !canvas.isConnected) {
            return;
          }
          if (!forecast || forecast.numHours === 0) {
            this.showEmptyState(canvas, loadingEl, model);
            continue;
          }
          this.buildPointForecastChart(layerKey, canvas, loadingEl, forecast, poiKey, model, units, tz, state, subKey, horizonDays, startMs, startDayKey, reqId, currentTheme);
        }
      } catch (err) {
        if (this.pendingRequests.get(layerKey) === reqId) {
          console.warn(`Failed to build legend trend chart for ${layerKey}:`, err);
          this.showEmptyState(canvas, loadingEl, model);
        }
      }
    }
  }

  private static showEmptyState(
    canvas: HTMLCanvasElement,
    loadingEl: HTMLElement | null,
    model: string,
    fallbackRequiredTier: AccessTier = getRequiredTierForLayer('forecast_point_tiles')
  ): void {
    canvas.style.display = 'none';
    if (loadingEl) {
      const state = store.getState();
      const userTier = TierAccessService.getUserAccessTier(state);
      const denial = ForecastBinaryService.lastAccessDenial;
      const effectiveReqTier = denial?.requiredTier || fallbackRequiredTier;
      const isDenied = Boolean(denial) || !hasTierAccess(userTier, effectiveReqTier) || ForecastBinaryService.lastAuthDenied;

      loadingEl.style.display = 'flex';
      if (isDenied) {
        loadingEl.style.flexDirection = 'column';
        loadingEl.style.alignItems = 'center';
        loadingEl.style.justifyContent = 'center';
        loadingEl.style.padding = '4px 6px';
        loadingEl.style.width = '100%';
        loadingEl.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial,
          requiredTier: effectiveReqTier,
          featureLabel: 'hourly forecast trend charts',
          compact: true,
        });
      } else {
        loadingEl.style.color = '#f87171';
        loadingEl.innerText = `Forecast trend data not available for ${model.toUpperCase()}`;
      }
    }
  }

  private static getLocalDayStartUtcMs(date: Date, timeZone: string): number {
    const dFmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const parts = dFmt.formatToParts(date);
    const Y = parseInt(parts.find(p => p.type === 'year')!.value, 10);
    const M = parseInt(parts.find(p => p.type === 'month')!.value, 10);
    const D = parseInt(parts.find(p => p.type === 'day')!.value, 10);

    const approxUtc = Date.UTC(Y, M - 1, D, 0, 0, 0);
    const p2 = dFmt.formatToParts(new Date(approxUtc));
    const y2 = parseInt(p2.find(p => p.type === 'year')!.value, 10);
    const m2 = parseInt(p2.find(p => p.type === 'month')!.value, 10);
    const d2 = parseInt(p2.find(p => p.type === 'day')!.value, 10);
    const h2 = parseInt(p2.find(p => p.type === 'hour')!.value, 10) % 24;
    const min2 = parseInt(p2.find(p => p.type === 'minute')!.value, 10);

    const targetDayUtc = Date.UTC(Y, M - 1, D, 0, 0, 0);
    const parsedDayUtc = Date.UTC(y2, m2 - 1, d2, 0, 0, 0);

    let diffHours = h2 + min2 / 60;
    if (parsedDayUtc > targetDayUtc) diffHours += 24;
    if (parsedDayUtc < targetDayUtc) diffHours -= 24;

    return approxUtc - diffHours * 3600 * 1000;
  }

  private static buildHourlyGrid(
    startMs: number,
    horizonDays: number
  ): { timestamps: number[] } {
    // 8 past hours + horizonDays * 24 future hours
    const totalHours = 8 + horizonDays * 24;
    const timestamps = new Array<number>(totalHours + 1);
    for (let h = 0; h <= totalHours; h++) {
      timestamps[h] = startMs + h * 3600 * 1000;
    }
    return { timestamps };
  }

  private static resampleSeries(
    rawOffsets: number[],
    rawValues: ArrayLike<number>,
    cycleEpoch: number,
    timestamps: number[],
    isAngle: boolean = false
  ): (number | null)[] {
    const count = timestamps.length;
    const resampled = new Array<number | null>(count);
    const n = rawOffsets.length;

    if (n === 0) return resampled;
    if (n === 1) {
      resampled.fill(Number(rawValues[0]));
      return resampled;
    }

    const cycleEpochMs = cycleEpoch * 1000;
    let k = 0;

    for (let i = 0; i < count; i++) {
      const targetOffset = (timestamps[i] - cycleEpochMs) / 3600000;

      if (targetOffset <= rawOffsets[0]) {
        resampled[i] = Number(rawValues[0]);
        continue;
      }
      if (targetOffset >= rawOffsets[n - 1]) {
        // Yield null past the model's actual forecast horizon (with 0.5h tolerance)
        // so the chart terminates cleanly rather than creating a flat line.
        if (targetOffset > rawOffsets[n - 1] + 0.5) {
          resampled[i] = null;
        } else {
          resampled[i] = Number(rawValues[n - 1]);
        }
        continue;
      }

      while (k < n - 1 && rawOffsets[k + 1] < targetOffset) {
        k++;
      }

      const o0 = rawOffsets[k];
      const o1 = rawOffsets[k + 1];
      const v0 = Number(rawValues[k]);
      const v1 = Number(rawValues[k + 1]);

      if (o1 === o0 || targetOffset === o0) {
        resampled[i] = v0;
      } else if (targetOffset === o1) {
        resampled[i] = v1;
      } else {
        const t = (targetOffset - o0) / (o1 - o0);
        if (isAngle) {
          const delta = ((((v1 - v0) + 540) % 360) - 180);
          resampled[i] = Math.round(((v0 + t * delta) + 360) % 360);
        } else {
          resampled[i] = Math.round((v0 * (1 - t) + v1 * t) * 10) / 10;
        }
      }
    }

    return resampled;
  }

  private static formatLabels(timestamps: number[], tz: string, isImperial: boolean): string[] {
    return timestamps.map(ts => {
      const d = new Date(ts);
      const weekdayStr = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
      const hourStr = d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: isImperial, timeZone: tz }).replace(' ', '');
      return `${weekdayStr} ${hourStr}`;
    });
  }

  private static findActiveIndex(timestamps: number[], currentTimeStep: Date | null, isDaily: boolean = false): number {
    if (!currentTimeStep || timestamps.length < 2) return -1;
    const currentMs = currentTimeStep.getTime();
    if (isDaily) {
      const startMs = timestamps[0];
      const endMs = timestamps[timestamps.length - 1];
      if (currentMs < startMs - 1800000 || currentMs > endMs + 1800000) return -1;
      const numDays = timestamps.length - 1;
      for (let d = 0; d < numDays; d++) {
        const d0 = timestamps[d];
        const d1 = timestamps[d + 1];
        if (currentMs >= d0 && currentMs <= d1) {
          const frac = (currentMs - d0) / (d1 - d0);
          return d - 0.5 + frac;
        }
      }
      if (currentMs < startMs) return -0.5;
      if (currentMs > endMs) return numDays - 0.5;
      return -1;
    }
    const startMs = timestamps[0];
    const endMs = timestamps[timestamps.length - 1];
    if (currentMs < startMs - 1800000 || currentMs > endMs + 1800000) return -1;
    const fracIdx = (currentMs - startMs) / 3600000;
    return Math.max(0, Math.min(fracIdx, timestamps.length - 1));
  }

  private static resolveActiveDailyForecast(
    targetProduct: string,
    model: string,
    state: AppState,
    dateStrings: string[],
    timestamps: number[],
    horizonDays: number,
    tz: string,
    evMorningKey: string,
    evEveningKey: string
  ): { activeDayIdx: number; isMorning: boolean } {
    const current = state.currentTimeStep;
    if (!current) return { activeDayIdx: -1, isMorning: true };

    let activeEvent = '';
    let activeDayIdx = -1;

    if (state.webcamsEnabled && state.selectedWebcamDate) {
      activeDayIdx = dateStrings.indexOf(state.selectedWebcamDate);
      let isMorn = true;
      try {
        const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false });
        const localHour = parseInt(dFmt.format(current), 10);
        isMorn = (localHour === 24 ? 0 : localHour) < 12;
      } catch {
        isMorn = current.getUTCHours() < 12;
      }
      activeEvent = isMorn ? evMorningKey : evEveningKey;
    } else if (state.manifest) {
      const defaultModel = 'gw_v2_blend_50';
      const clampedFrame = ManifestTimelineHelper.getClampedFrame(state.manifest, targetProduct, model, current)
        || ManifestTimelineHelper.getClampedFrame(state.manifest, targetProduct, defaultModel, current)
        || ManifestTimelineHelper.getClampedFrame(state.manifest, targetProduct, 'gw_v2_blend_50', current);
      if (clampedFrame) {
        activeEvent = clampedFrame.hourStr;
        activeDayIdx = dateStrings.indexOf(clampedFrame.dateStr);
      }
    }

    if (activeDayIdx === -1) {
      const activeIdx = this.findActiveIndex(timestamps, current, true);
      activeDayIdx = (activeIdx !== -1) ? Math.min(horizonDays - 1, Math.max(0, Math.floor(activeIdx + 0.5))) : -1;
    }

    if (!activeEvent) {
      let isMorn = true;
      try {
        const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false });
        const localHour = parseInt(dFmt.format(current), 10);
        isMorn = (localHour === 24 ? 0 : localHour) < 12;
      } catch {
        isMorn = current.getUTCHours() < 12;
      }
      activeEvent = isMorn ? evMorningKey : evEveningKey;
    }

    const isMorning = (activeEvent === evMorningKey || activeEvent.includes('morning') || activeEvent === 'sunrise');
    return { activeDayIdx, isMorning };
  }

  private static createCursorPlugin(layerKey: string, getActiveIdx: () => number) {
    return {
      id: `legendCursor_${layerKey}`,
      afterDraw: (chart: any) => {
        const activeIdx = getActiveIdx();
        if (activeIdx !== -1 && chart.scales.x) {
          const ctx = chart.ctx;
          const x = chart.scales.x.getPixelForValue(activeIdx);
          const topY = chart.chartArea.top;
          const bottomY = chart.chartArea.bottom;
          const clampedX = Math.max(chart.chartArea.left, Math.min(x, chart.chartArea.right));
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(clampedX, topY);
          ctx.lineTo(clampedX, bottomY);
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = this.getThemeColors().cursorColor;
          ctx.setLineDash([3, 3]);
          ctx.stroke();
          ctx.restore();
        }
      }
    };
  }

  private static createReferenceLinesPlugin(layerKey: string) {
    return {
      id: `legendRefLines_${layerKey}`,
      afterDraw: (chart: any) => {
        const state = store.getState();
        if (state.trendRefLinesEnabled === false) {
          return;
        }

        const isLineChart = chart.config.type === 'line' ||
          (chart.data.datasets && chart.data.datasets.some((d: any) => d.type === 'line'));
        if (!isLineChart) {
          return;
        }

        const yScale = chart.scales.y;
        if (!yScale || !chart.chartArea) {
          return;
        }

        const datasets = chart.data.datasets || [];
        let minVal = Infinity;
        let maxVal = -Infinity;
        let hasValidData = false;

        for (const ds of datasets) {
          if (ds.hidden) continue;
          if (ds.type === 'bar') continue;
          if (ds.label && typeof ds.label === 'string' && ds.label.includes('POI Elevation')) continue;
          if (!Array.isArray(ds.data)) continue;

          for (const v of ds.data) {
            if (v !== null && v !== undefined && typeof v === 'number' && !isNaN(v) && isFinite(v)) {
              if (v < minVal) minVal = v;
              if (v > maxVal) maxVal = v;
              hasValidData = true;
            }
          }
        }

        if (!hasValidData || minVal === Infinity || maxVal === -Infinity) {
          return;
        }

        const midVal = (minVal + maxVal) / 2;

        const formatVal = (val: number): string => {
          if (yScale.options?.ticks?.callback && typeof yScale.options.ticks.callback === 'function') {
            try {
              const res = yScale.options.ticks.callback.call(yScale, val, 0, []);
              if (typeof res === 'string' && res.trim().length > 0) {
                return res.trim();
              }
            } catch (_) {}
          }
          if (Math.abs(val) >= 100 || Number.isInteger(val)) {
            return `${Math.round(val)}`;
          }
          if (Math.abs(val) >= 10) {
            return `${Math.round(val * 10) / 10}`;
          }
          return `${Math.round(val * 100) / 100}`;
        };

        const ctx = chart.ctx;
        const leftX = chart.chartArea.left;
        const rightX = chart.chartArea.right;
        const topY = chart.chartArea.top;
        const bottomY = chart.chartArea.bottom;

        const isLight = themeService.isLightPalette();

        const maxColor = isLight ? '#e11d48' : '#fb7185';
        const midColor = isLight ? '#0284c7' : '#38bdf8';
        const minColor = isLight ? '#2563eb' : '#60a5fa';

        const maxBg = isLight ? 'rgba(255, 241, 242, 0.94)' : 'rgba(28, 14, 22, 0.90)';
        const midBg = isLight ? 'rgba(240, 249, 255, 0.94)' : 'rgba(10, 24, 38, 0.90)';
        const minBg = isLight ? 'rgba(239, 246, 255, 0.94)' : 'rgba(14, 20, 36, 0.90)';

        const linesToDraw: Array<{
          type: 'max' | 'mid' | 'min';
          val: number;
          label: string;
          strokeColor: string;
          bgColor: string;
          dash: number[];
        }> = [];

        if (maxVal === minVal) {
          linesToDraw.push({
            type: 'max',
            val: maxVal,
            label: `VAL: ${formatVal(maxVal)}`,
            strokeColor: midColor,
            bgColor: midBg,
            dash: [3, 3]
          });
        } else {
          linesToDraw.push({
            type: 'max',
            val: maxVal,
            label: `MAX: ${formatVal(maxVal)}`,
            strokeColor: maxColor,
            bgColor: maxBg,
            dash: [4, 3]
          });
          linesToDraw.push({
            type: 'mid',
            val: midVal,
            label: `MID: ${formatVal(midVal)}`,
            strokeColor: midColor,
            bgColor: midBg,
            dash: [2, 3]
          });
          linesToDraw.push({
            type: 'min',
            val: minVal,
            label: `MIN: ${formatVal(minVal)}`,
            strokeColor: minColor,
            bgColor: minBg,
            dash: [4, 3]
          });
        }

        ctx.save();
        ctx.font = 'bold 8px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';

        const renderedItems = linesToDraw.map(item => {
          const rawY = yScale.getPixelForValue(item.val);
          const y = Math.max(topY + 7, Math.min(rawY, bottomY - 7));
          const textMetrics = ctx.measureText(item.label);
          const badgeW = textMetrics.width + 10;
          const badgeH = 13;
          return {
            ...item,
            rawY,
            y,
            badgeW,
            badgeH,
            badgeY: y - badgeH / 2,
            staggerOffset: 0
          };
        });

        // Collision avoidance: if vertical distance < 14px, stagger horizontally
        for (let i = 1; i < renderedItems.length; i++) {
          const prev = renderedItems[i - 1];
          const curr = renderedItems[i];
          if (Math.abs(curr.badgeY - prev.badgeY) < 14) {
            curr.staggerOffset = prev.badgeW + 6;
          }
        }

        for (const item of renderedItems) {
          // 1. Horizontal dashed line across chartArea
          ctx.beginPath();
          ctx.moveTo(leftX, item.y);
          ctx.lineTo(rightX, item.y);
          ctx.lineWidth = 1.1;
          ctx.strokeStyle = item.strokeColor;
          ctx.setLineDash(item.dash);
          ctx.globalAlpha = 0.70;
          ctx.stroke();

          // 2. Numerical badge on the right
          ctx.setLineDash([]);
          ctx.globalAlpha = 1.0;
          const badgeX = rightX - item.badgeW - (item.staggerOffset || 3);

          ctx.beginPath();
          if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(badgeX, item.badgeY, item.badgeW, item.badgeH, 3);
          } else {
            ctx.rect(badgeX, item.badgeY, item.badgeW, item.badgeH);
          }
          ctx.fillStyle = item.bgColor;
          ctx.fill();
          ctx.lineWidth = 1;
          ctx.strokeStyle = item.strokeColor;
          ctx.stroke();

          ctx.fillStyle = item.strokeColor;
          ctx.fillText(item.label, badgeX + 5, item.y);
        }

        ctx.restore();
      }
    };
  }

  public static getTimeTickInfo(timestamps: number[], tz: string, isImperial: boolean): Map<number, TimeTickInfo> {
    const map = new Map<number, TimeTickInfo>();
    if (!timestamps || timestamps.length === 0) return map;

    const dFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      weekday: 'short'
    });

    for (let i = 0; i < timestamps.length; i++) {
      const ts = timestamps[i];
      const parts = dFmt.formatToParts(new Date(ts));
      const rawH = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      const hour = rawH === 24 ? 0 : rawH;
      const min = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);

      if (min !== 0) continue;

      const isMidnight = (hour === 0);
      const isNoon = (hour === 12);

      if (isMidnight || isNoon) {
        const weekday = parts.find(p => p.type === 'weekday')?.value || '';
        let label = '';
        if (isMidnight) {
          label = isImperial ? `${weekday} 12a` : `${weekday} 00:00`;
        } else {
          label = isImperial ? '12p' : '12:00';
        }
        map.set(i, {
          index: i,
          timestamp: ts,
          isMidnight,
          isNoon,
          label
        });
      }
    }
    return map;
  }

  public static createTimeTickMarksPlugin(layerKey: string, timeTicksMap: Map<number, TimeTickInfo>) {
    return {
      id: `legendTimeTicks_${layerKey}`,
      beforeInit: (chart: any) => {
        chart.$timeTicksMap = timeTicksMap;
      },
      beforeDraw: (chart: any) => {
        chart.$timeTicksMap = timeTicksMap;
        if (!chart.scales.x || !chart.chartArea || !timeTicksMap || timeTicksMap.size === 0) return;
        const ctx = chart.ctx;
        const topY = chart.chartArea.top;
        const bottomY = chart.chartArea.bottom;
        const isLight = themeService.isLightPalette();

        // Midnight: crisp solid line separating days
        const midnightColor = isLight ? 'rgba(15, 23, 42, 0.22)' : 'rgba(255, 255, 255, 0.20)';
        // Noon: subtle dashed line indicating midday
        const noonColor = isLight ? 'rgba(15, 23, 42, 0.10)' : 'rgba(255, 255, 255, 0.08)';

        ctx.save();
        for (const [idx, item] of timeTicksMap.entries()) {
          const x = chart.scales.x.getPixelForValue(idx);
          if (x < chart.chartArea.left || x > chart.chartArea.right) continue;

          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          if (item.isMidnight) {
            ctx.strokeStyle = midnightColor;
            ctx.lineWidth = 1.2;
            ctx.setLineDash([]);
          } else {
            ctx.strokeStyle = noonColor;
            ctx.lineWidth = 1.0;
            ctx.setLineDash([2, 3]);
          }
          ctx.stroke();
        }
        ctx.restore();
      },
      afterDraw: (chart: any) => {
        if (!chart.scales.x || !chart.chartArea || !timeTicksMap || timeTicksMap.size === 0) return;
        const ctx = chart.ctx;
        const bottomY = chart.chartArea.bottom;
        const isLight = themeService.isLightPalette();

        const midnightTickColor = isLight ? 'rgba(15, 23, 42, 0.60)' : 'rgba(255, 255, 255, 0.60)';
        const noonTickColor = isLight ? 'rgba(15, 23, 42, 0.30)' : 'rgba(255, 255, 255, 0.30)';

        ctx.save();
        for (const [idx, item] of timeTicksMap.entries()) {
          const x = chart.scales.x.getPixelForValue(idx);
          if (x < chart.chartArea.left || x > chart.chartArea.right) continue;

          const tickLen = item.isMidnight ? 5 : 3;
          ctx.beginPath();
          ctx.moveTo(x, bottomY);
          ctx.lineTo(x, bottomY + tickLen);
          ctx.strokeStyle = item.isMidnight ? midnightTickColor : noonTickColor;
          ctx.lineWidth = item.isMidnight ? 1.2 : 1.0;
          ctx.setLineDash([]);
          ctx.stroke();
        }
        ctx.restore();
      }
    };
  }

  public static getThemeColors(): {
    tickColor: string;
    gridColor: string;
    subtleGridColor: string;
    cursorColor: string;
  } {
    const isLight = themeService.isLightPalette();
    const currentTheme = themeService.getTheme();
    const cursorColor = currentTheme === 'scifi' ? '#38bdf8' : (currentTheme === 'earth' ? '#86a37c' : (currentTheme === 'forest' ? '#22c55e' : (currentTheme === 'paper' ? '#1d4ed8' : '#00d4ff')));
    return {
      tickColor: isLight ? '#190f05' : 'rgba(255, 255, 255, 0.65)',
      gridColor: isLight ? 'rgba(90, 67, 45, 0.20)' : 'rgba(255, 255, 255, 0.06)',
      subtleGridColor: isLight ? 'rgba(90, 67, 45, 0.10)' : 'rgba(255, 255, 255, 0.04)',
      cursorColor
    };
  }

  public static getCommonXScale(timeTicksMap?: Map<number, TimeTickInfo>) {
    const ticksMap = timeTicksMap || this.currentTimeTicksMap;

    return {
      grid: { display: false },
      ticks: {
        color: (ctx: any) => {
          const chart = ctx.chart;
          const activeMap = ticksMap || chart?.$timeTicksMap;
          if (!activeMap) return this.getThemeColors().tickColor;
          const info = activeMap.get(ctx.index);
          if (!info) return 'transparent';
          const isLight = themeService.isLightPalette();
          if (info.isMidnight) return isLight ? '#0f172a' : '#f8fafc';
          return isLight ? '#64748b' : '#94a3b8';
        },
        font: (ctx: any) => {
          const chart = ctx.chart;
          const activeMap = ticksMap || chart?.$timeTicksMap;
          const info = activeMap ? activeMap.get(ctx.index) : null;
          return {
            size: 8,
            weight: (info && info.isMidnight) ? ('bold' as const) : ('normal' as const)
          };
        },
        maxRotation: 0,
        autoSkip: false,
        callback: function(val: any, index: number) {
          const chart = (this as any).chart;
          const activeMap = ticksMap || chart?.$timeTicksMap;
          if (!activeMap) return `${val}`;
          const info = activeMap.get(index);
          if (!info) return '';
          const chartWidth = chart?.width || 800;
          if (info.isMidnight) {
            if (chartWidth < 450 && info.label.includes(' ')) {
              return info.label.split(' ')[0]; // e.g. "Mon"
            }
            return info.label; // e.g. "Mon 12a"
          }
          return info.label; // "12p"
        }
      }
    };
  }

  private static getCommonScales(yMax?: number, yMin: number = 0, timeTicksMap?: Map<number, TimeTickInfo>) {
    const themeColors = this.getThemeColors();
    return {
      x: this.getCommonXScale(timeTicksMap),
      y: {
        min: yMin,
        max: yMax,
        grid: { color: themeColors.gridColor },
        ticks: {
          color: themeColors.tickColor,
          font: { size: 9 },
          callback: (val: any) => `${val}`
        }
      }
    };
  }

  /**
   * Maps a sunrise/sunset/golden hour score (0-100) to its exact visual palette color,
   * matching apply_blend50_gradient from generators/color_schemes.py and map legend:
   * - 0 to 25: Deep Blue #0046a0 (0, 70, 160) -> Cyan #00b4d8 (0, 180, 216)
   * - 25 to 50: Cyan #00b4d8 (0, 180, 216) -> Yellow #ffff00 (255, 255, 0)
   * - 50 to 75: Yellow #ffff00 (255, 255, 0) -> Orange #ff8000 (255, 128, 0)
   * - 75 to 95: Orange #ff8000 (255, 128, 0) -> Red #ff0000 (255, 0, 0)
   * - 95 to 100: Pure Red #ff0000 (255, 0, 0) -> Deep Red #b71c1c (183, 28, 28)
   */
  private static getSolarScoreColor(score: number, alpha: number = 0.85): string {
    const s = Math.max(0, Math.min(100, Math.round(score)));
    let r = 0, g = 70, b = 160;

    if (s <= 0) {
      return `rgba(0, 70, 160, ${alpha * 0.4})`;
    } else if (s < 25) {
      const t = s / 25.0;
      r = 0;
      g = Math.round(70 + (180 - 70) * t);
      b = Math.round(160 + (216 - 160) * t);
    } else if (s < 50) {
      const t = (s - 25) / 25.0;
      r = Math.round(255 * t);
      g = Math.round(180 + (255 - 180) * t);
      b = Math.round(216 * (1 - t));
    } else if (s < 75) {
      const t = (s - 50) / 25.0;
      r = 255;
      g = Math.round(255 - (255 - 128) * t);
      b = 0;
    } else if (s < 95) {
      const t = (s - 75) / 20.0;
      r = 255;
      g = Math.round(128 * (1 - t));
      b = 0;
    } else {
      const t = (s - 95) / 5.0;
      r = Math.round(255 - (255 - 183) * t);
      g = Math.round(28 * t);
      b = Math.round(28 * t);
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private static async buildDailyDiscreteChart(
    layerKey: string,
    canvas: HTMLCanvasElement,
    loadingEl: HTMLElement | null,
    poi: any,
    poiKey: string,
    model: string,
    units: string,
    tz: string,
    state: AppState,
    _optionsSubKey: string,
    horizonDays: number,
    startMs: number,
    startDayKey: string,
    reqId: number,
    themeId: string = 'dark'
  ): Promise<void> {
    let targetProduct = layerKey;
    let evMorning: { key: string; label: string; color: string; activeColor: string };
    let evEvening: { key: string; label: string; color: string; activeColor: string };

    if (layerKey === 'sunrise') {
      targetProduct = 'sunrise';
      evMorning = { key: 'sunrise', label: 'Sunrise Vibrancy', color: '#f59e0b', activeColor: '#fde047' };
      evEvening = { key: 'sunset', label: 'Sunset Vibrancy', color: '#f97316', activeColor: '#fdba74' };
    } else if (layerKey === 'goldenhour' || (layerKey === 'alpenglow' && state.alpenglowMode === 'goldenhour')) {
      targetProduct = 'goldenhour';
      evMorning = { key: 'goldenhour_morning', label: 'Morning Golden Hour', color: '#eab308', activeColor: '#fef08a' };
      evEvening = { key: 'goldenhour_evening', label: 'Evening Golden Hour', color: '#f59e0b', activeColor: '#fde047' };
    } else {
      targetProduct = 'alpenglow';
      evMorning = { key: 'alpenglow_morning', label: 'Morning Alpenglow', color: '#ec4899', activeColor: '#f472b6' };
      evEvening = { key: 'alpenglow_evening', label: 'Evening Alpenglow', color: '#e11d48', activeColor: '#fda4af' };
    }

    const dayStarts: number[] = [];
    const dayLabels: string[] = [];
    const dateStrings: string[] = [];

    const firstDayStart = this.getLocalDayStartUtcMs(new Date(startMs), tz);
    for (let d = 0; d < horizonDays; d++) {
      const approxDayMidMs = firstDayStart + d * 86400000 + 43200000;
      const dStart = this.getLocalDayStartUtcMs(new Date(approxDayMidMs), tz);
      dayStarts.push(dStart);

      const dObj = new Date(dStart + 43200000);
      dateStrings.push(dObj.toLocaleDateString('en-CA', { timeZone: tz }));
      const weekday = dObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
      const dayMonth = dObj.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: tz });
      dayLabels.push(`${weekday} ${dayMonth}`);
    }

    const lastDayMidMs = firstDayStart + horizonDays * 86400000 + 43200000;
    const lastDayEnd = this.getLocalDayStartUtcMs(new Date(lastDayMidMs), tz);
    const timestamps = [...dayStarts, lastDayEnd];

    const morningScores: number[] = [];
    const eveningScores: number[] = [];

    const cacheKey = `${poiKey}_${targetProduct}_${model}_${horizonDays}_${firstDayStart}`;
    const cached = LegendTrendChartService.solarScoresCache.get(cacheKey);

    if (cached) {
      morningScores.push(...cached.morningScores);
      eveningScores.push(...cached.eveningScores);
    } else {
      // Attempt to read solar scores directly from spatial binary point tiles
      let pointData: DecodedPointForecast | null = null;
      try {
        pointData = await ForecastBinaryService.getForecastForPoint(poi.lat, poi.lng, state.manifest!, model);
      } catch (_e) {
        pointData = null;
      }

      if (this.pendingRequests.get(layerKey) !== reqId || !canvas.isConnected) {
        return;
      }

      const solarReqTier = getRequiredTierForLayer(targetProduct);
      const userTierForSolar = TierAccessService.getUserAccessTier(state);
      if (!pointData && (ForecastBinaryService.lastAccessDenial || !hasTierAccess(userTierForSolar, solarReqTier))) {
        this.showEmptyState(canvas, loadingEl, model, solarReqTier);
        return;
      }

      const hasBinaryScores = Boolean(
        pointData && (
          (targetProduct === 'sunrise' && (pointData.sunriseScore?.some(v => v > 0) || pointData.sunsetScore?.some(v => v > 0))) ||
          (targetProduct === 'goldenhour' && (pointData.goldenHourMorning?.some(v => v > 0) || pointData.goldenHourEvening?.some(v => v > 0))) ||
          (targetProduct === 'alpenglow' && (pointData.alpenglowMorning?.some(v => v > 0) || pointData.alpenglowEvening?.some(v => v > 0)))
        )
      );

      if (hasBinaryScores && pointData) {
        for (const dateStr of dateStrings) {
          let mScore = 0;
          let eScore = 0;
          const matchingHours: number[] = [];
          for (let h = 0; h < pointData.numHours; h++) {
            const stepDate = new Date((pointData.cycleEpoch + h * 3600) * 1000);
            const stepDateStr = stepDate.toLocaleDateString('en-CA', { timeZone: tz });
            if (stepDateStr === dateStr) {
              matchingHours.push(h);
            }
          }
          if (matchingHours.length > 0) {
            const repH = matchingHours[Math.floor(matchingHours.length / 2)];
            if (targetProduct === 'sunrise') {
              mScore = pointData.sunriseScore?.[repH] ?? 0;
              eScore = pointData.sunsetScore?.[repH] ?? 0;
            } else if (targetProduct === 'goldenhour') {
              mScore = pointData.goldenHourMorning?.[repH] ?? 0;
              eScore = pointData.goldenHourEvening?.[repH] ?? 0;
            } else {
              mScore = pointData.alpenglowMorning?.[repH] ?? 0;
              eScore = pointData.alpenglowEvening?.[repH] ?? 0;
            }
          }
          morningScores.push(mScore);
          eveningScores.push(eScore);
        }
      } else {
        const frames = ManifestTimelineHelper.getProductFrames(state.manifest!, targetProduct as any, model);
        const availableKeys = new Set(frames.map(f => `${f.dateStr}_${f.hourStr}`));

        const probeResults = await Promise.all(dateStrings.map(async (dateStr) => {
          let mScore = 0;
          const mornKey = `${dateStr}_${evMorning.key}`;
          if (availableKeys.has(mornKey)) {
            try {
              const rawTileUrl = getTileUrl(state.manifest!, targetProduct as any, model, dateStr, evMorning.key);
              mScore = await getGenericScoreAt(poi.lat, poi.lng, rawTileUrl);
            } catch (err) {
              console.warn(`[LegendTrendChartService] Failed to probe ${targetProduct} ${dateStr} ${evMorning.key}:`, err);
              mScore = 0;
            }
          }

          let eScore = 0;
          const eveKey = `${dateStr}_${evEvening.key}`;
          if (availableKeys.has(eveKey)) {
            try {
              const rawTileUrl = getTileUrl(state.manifest!, targetProduct as any, model, dateStr, evEvening.key);
              eScore = await getGenericScoreAt(poi.lat, poi.lng, rawTileUrl);
            } catch (err) {
              console.warn(`[LegendTrendChartService] Failed to probe ${targetProduct} ${dateStr} ${evEvening.key}:`, err);
              eScore = 0;
            }
          }

          return { mScore, eScore };
        }));

        if (this.pendingRequests.get(layerKey) !== reqId || !canvas.isConnected) {
          return;
        }

        for (const res of probeResults) {
          morningScores.push(res.mScore);
          eveningScores.push(res.eScore);
        }
      }

      LegendTrendChartService.solarScoresCache.set(cacheKey, {
        morningScores: [...morningScores],
        eveningScores: [...eveningScores]
      });
    }

    const activeIdx = this.findActiveIndex(timestamps, state.currentTimeStep, true);
    const { activeDayIdx, isMorning } = this.resolveActiveDailyForecast(
      targetProduct,
      model,
      state,
      dateStrings,
      timestamps,
      horizonDays,
      tz,
      evMorning.key,
      evEvening.key
    );

    const getMorningBg = (actDay: number) => morningScores.map((score, i) => {
      const isAct = i === actDay;
      return LegendTrendChartService.getSolarScoreColor(score, isAct ? 1.0 : 0.80);
    });
    const getMorningBorder = (actDay: number) => morningScores.map((score, i) => {
      return i === actDay ? '#ffffff' : LegendTrendChartService.getSolarScoreColor(score, 1.0);
    });
    const getMorningBorderWidth = (actDay: number) => morningScores.map((_, i) => (i === actDay ? 2 : 1));

    const morningDataset: any = {
      type: 'bar',
      label: evMorning.label,
      data: morningScores,
      backgroundColor: getMorningBg(isMorning ? activeDayIdx : -1),
      borderColor: getMorningBorder(isMorning ? activeDayIdx : -1),
      borderWidth: getMorningBorderWidth(isMorning ? activeDayIdx : -1),
      hoverBackgroundColor: morningScores.map(s => LegendTrendChartService.getSolarScoreColor(s, 1.0)),
      hoverBorderColor: '#ffffff',
      hoverBorderWidth: 2,
      borderRadius: 3,
      maxBarThickness: 20,
      minBarLength: 4
    };
    morningDataset._updateColors = (actDay: number, isMorn: boolean = true) => {
      const targetDay = isMorn ? actDay : -1;
      morningDataset.backgroundColor = getMorningBg(targetDay);
      morningDataset.borderColor = getMorningBorder(targetDay);
      morningDataset.borderWidth = getMorningBorderWidth(targetDay);
    };

    const getEveningBg = (actDay: number) => eveningScores.map((score, i) => {
      const isAct = i === actDay;
      return LegendTrendChartService.getSolarScoreColor(score, isAct ? 1.0 : 0.80);
    });
    const getEveningBorder = (actDay: number) => eveningScores.map((score, i) => {
      return i === actDay ? '#ffffff' : LegendTrendChartService.getSolarScoreColor(score, 1.0);
    });
    const getEveningBorderWidth = (actDay: number) => eveningScores.map((_, i) => (i === actDay ? 2 : 1));

    const eveningDataset: any = {
      type: 'bar',
      label: evEvening.label,
      data: eveningScores,
      backgroundColor: getEveningBg(!isMorning ? activeDayIdx : -1),
      borderColor: getEveningBorder(!isMorning ? activeDayIdx : -1),
      borderWidth: getEveningBorderWidth(!isMorning ? activeDayIdx : -1),
      hoverBackgroundColor: eveningScores.map(s => LegendTrendChartService.getSolarScoreColor(s, 1.0)),
      hoverBorderColor: '#ffffff',
      hoverBorderWidth: 2,
      borderRadius: 3,
      maxBarThickness: 20,
      minBarLength: 4
    };
    eveningDataset._updateColors = (actDay: number, isMorn: boolean = false) => {
      const targetDay = !isMorn ? actDay : -1;
      eveningDataset.backgroundColor = getEveningBg(targetDay);
      eveningDataset.borderColor = getEveningBorder(targetDay);
      eveningDataset.borderWidth = getEveningBorderWidth(targetDay);
    };

    const cursorPlugin = this.createCursorPlugin(layerKey, () => this.charts.get(layerKey)?.activeIdx ?? activeIdx);
    const themeColors = this.getThemeColors();

    try {
      const existing = Chart.getChart(canvas);
      if (existing) existing.destroy();
    } catch (_e) {}

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: dayLabels,
        datasets: [morningDataset, eveningDataset]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              boxWidth: 8,
              boxHeight: 8,
              font: { size: 8 },
              color: themeColors.tickColor,
              generateLabels: (c: any) => [
                {
                  text: evMorning.label,
                  fillStyle: evMorning.color,
                  strokeStyle: '#ffffff',
                  lineWidth: 1,
                  hidden: !c.isDatasetVisible(0),
                  datasetIndex: 0
                },
                {
                  text: evEvening.label,
                  fillStyle: evEvening.color,
                  strokeStyle: '#ffffff',
                  lineWidth: 1,
                  hidden: !c.isDatasetVisible(1),
                  datasetIndex: 1
                }
              ]
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const score = ctx.parsed.y;
                const tier = getGoldenWindowRating(score);
                return ` ${ctx.dataset.label}: ${score}/100 (${tier})`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: themeColors.tickColor,
              font: { size: 8 }
            }
          },
          y: {
            min: 0,
            max: 100,
            grid: { color: themeColors.gridColor },
            ticks: {
              color: themeColors.tickColor,
              font: { size: 8 },
              stepSize: 25,
              callback: (val: any) => `${val}`
            }
          }
        }
      },
      plugins: [cursorPlugin]
    });

    if (this.pendingRequests.get(layerKey) !== reqId || !canvas.isConnected) {
      try { chart.destroy(); } catch (_e) {}
      return;
    }

    if (loadingEl) loadingEl.style.display = 'none';
    canvas.style.display = 'block';

    this.charts.set(layerKey, {
      chart,
      poiKey,
      model,
      units,
      activeIdx,
      timestamps,
      horizonDays,
      startDayKey,
      optionsSubKey: _optionsSubKey,
      isDaily: true,
      themeId,
      targetProduct,
      dateStrings,
      evMorningKey: evMorning.key,
      evEveningKey: evEvening.key
    });

    this.attachInteractionListeners(canvas, layerKey);
  }

  private static buildValleyFogChart(
    layerKey: string,
    canvas: HTMLCanvasElement,
    loadingEl: HTMLElement | null,
    fogData: DecodedValleyFog,
    poiKey: string,
    model: string,
    units: string,
    tz: string,
    state: AppState,
    horizonDays: number,
    startMs: number,
    startDayKey: string,
    reqId: number,
    themeId: string = 'dark'
  ): void {
    const isImperial = units === 'imperial';
    const rawOffsets = fogData.rawHourOffsets || getRawHourOffsetsForModel(model, fogData.numHours);
    const { timestamps } = this.buildHourlyGrid(startMs, horizonDays);
    const labels = this.formatLabels(timestamps, tz, isImperial);
    const timeTicksMap = LegendTrendChartService.getTimeTickInfo(timestamps, tz, isImperial);
    LegendTrendChartService.currentTimeTicksMap = timeTicksMap;
    const data = this.resampleSeries(rawOffsets, fogData.fogProbability, fogData.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));

    let activeIdx = this.findActiveIndex(timestamps, state.currentTimeStep);
    const cursorPlugin = this.createCursorPlugin(layerKey, () => this.charts.get(layerKey)?.activeIdx ?? activeIdx);

    const refLinesPlugin = this.createReferenceLinesPlugin(layerKey);
    const timeTicksPlugin = LegendTrendChartService.createTimeTickMarksPlugin(layerKey, timeTicksMap);

    try {
      const existing = Chart.getChart(canvas);
      if (existing) {
        existing.destroy();
      }
    } catch (_e) {
      // ignore
    }

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'Fog Probability',
          data,
          borderColor: '#b0bec5',
          backgroundColor: 'rgba(144, 164, 174, 0.25)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHitRadius: 6,
          pointBackgroundColor: '#eceff1',
          fill: true,
          tension: 0.3,
          spanGaps: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => ` Fog Probability: ${context.parsed.y}%`
            }
          }
        },
        scales: {
          x: this.getCommonScales().x,
          y: {
            min: 0,
            max: 100,
            grid: { color: 'rgba(255, 255, 255, 0.06)' },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 8 },
              callback: (val) => `${val}%`
            }
          }
        }
      },
      plugins: [timeTicksPlugin, cursorPlugin, refLinesPlugin]
    });

    LegendTrendChartService.currentTimeTicksMap = null;

    if (this.pendingRequests.get(layerKey) !== reqId || !canvas.isConnected) {
      try {
        chart.destroy();
      } catch (_e) {
        // ignore
      }
      return;
    }

    if (loadingEl) loadingEl.style.display = 'none';
    canvas.style.display = 'block';

    this.charts.set(layerKey, {
      chart,
      poiKey,
      model,
      units,
      activeIdx,
      timestamps,
      horizonDays,
      startDayKey,
      isDaily: false,
      themeId,
      refLinesEnabled: state.trendRefLinesEnabled !== false,
      timeTicksMap
    });

    this.attachInteractionListeners(canvas, layerKey);
  }

  private static buildDailyPrecipChart(
    canvas: HTMLCanvasElement,
    loadingEl: HTMLElement | null,
    forecast: DecodedPointForecast,
    poiKey: string,
    model: string,
    units: string,
    tz: string,
    state: AppState,
    optionsSubKey: string,
    horizonDays: number,
    startMs: number,
    startDayKey: string,
    reqId: number,
    themeId: string = 'dark'
  ): void {
    const isSnow = (optionsSubKey === '24h_snow');
    const isImperial = (units === 'imperial');
    const dataUnit = isSnow ? (isImperial ? 'in' : 'cm') : (isImperial ? 'in' : 'mm');
    const rawOffsets = forecast.rawHourOffsets || getRawHourOffsetsForModel(model, forecast.numHours);

    // Raw accumulation rates
    let rawSeries: number[];
    if (isSnow) {
      const numHours = forecast.numHours;
      const snowSeries = new Array<number>(numHours);
      const poi = state.selectedPoi;
      const targetElevFt = poi?.elev ?? 0;

      for (let h = 0; h < numHours; h++) {
        const baseSnowCmH = forecast.snowRateCmH?.[h] || 0;
        const precipMmH = forecast.precipRateMmH?.[h] || 0;

        if (precipMmH <= 0 && baseSnowCmH <= 0) {
          snowSeries[h] = 0;
          continue;
        }

        // Determine effective temperature at the target elevation
        let tempC = forecast.tempsC?.[h] ?? 10;
        if (targetElevFt > 0) {
          const flFt = forecast.freezingLevelFt?.[h] ?? 0;
          if (flFt > 0) {
            // Freezing level altitude: temperature at flFt is 0 deg C.
            // Standard environmental lapse rate above/below freezing level (-6.5 C per 1,000m / -1.98 C per 1,000 ft)
            tempC = -((targetElevFt - flFt) / 1000.0) * 1.98;
          } else if (forecast.tempsC?.[h] !== undefined) {
            // If freezing level is absent, apply standard lapse rate from sea-level/valley
            tempC = forecast.tempsC[h] - (targetElevFt / 1000.0) * 1.98;
          }
        }

        // Apply meteorological standard Kuchera Snow-to-Liquid Ratio (SLR)
        let elevSnowCmH = 0;
        if (precipMmH > 0) {
          let slr = 0;
          if (tempC <= 0) {
            // Freezing regime: 10:1 at 0C up to 30:1 at -15C or colder
            slr = Math.min(30.0, Math.max(5.0, 10.0 + (20.0 / 15.0) * (-tempC)));
          } else if (tempC <= 2.0) {
            // Transition regime (0C to 2C): linear taper down to 0:1
            slr = 10.0 * ((2.0 - tempC) / 2.0);
          }
          elevSnowCmH = (precipMmH * slr) / 10.0;
        }

        const effectiveSnowCmH = Math.max(baseSnowCmH, elevSnowCmH);
        snowSeries[h] = isImperial ? (effectiveSnowCmH / 2.54) : effectiveSnowCmH;
      }
      rawSeries = snowSeries;
    } else {
      rawSeries = isImperial
        ? forecast.precipRateMmH.map(v => v / 25.4)
        : forecast.precipRateMmH;
    }

    let labels: string[] = [];
    let barData: (number | null)[] = [];
    let cumData: (number | null)[] = [];
    let timestamps: number[] = [];
    let isDaily = false;
    let activeIdx = -1;

    if (horizonDays === 1) {
      // 1D Horizon: 8 past hours + 24 future hours
      const totalHours = 8 + 24;
      const hourTs: number[] = new Array(totalHours);
      for (let h = 0; h < totalHours; h++) {
        hourTs[h] = startMs + h * 3600 * 1000;
      }
      timestamps = hourTs;
      labels = this.formatLabels(hourTs, tz, isImperial);

      const hourlyVals = this.resampleSeries(rawOffsets, rawSeries, forecast.cycleEpoch, hourTs)
        .map(v => v === null ? null : Math.max(0, isImperial ? Math.round(v * 100) / 100 : Math.round(v * 10) / 10));

      let runningCum = 0;
      const cumVals: (number | null)[] = new Array(totalHours);
      for (let h = 0; h < totalHours; h++) {
        if (hourlyVals[h] !== null) runningCum += hourlyVals[h]!;
        cumVals[h] = hourlyVals[h] === null ? null : (isImperial ? Math.round(runningCum * 100) / 100 : Math.round(runningCum * 10) / 10);
      }

      barData = hourlyVals;
      cumData = cumVals;
      isDaily = false;
      activeIdx = this.findActiveIndex(hourTs, state.currentTimeStep, false);
    } else {
      // 3D or 5D Horizon: Daily 24h accumulation bars + cumulative curve across days
      isDaily = true;
      const dayStarts: number[] = [];
      const dayLabels: string[] = [];

      const firstDayStart = this.getLocalDayStartUtcMs(new Date(startMs), tz);
      for (let d = 0; d < horizonDays; d++) {
        const approxDayMidMs = firstDayStart + d * 86400000 + 43200000;
        const dStart = this.getLocalDayStartUtcMs(new Date(approxDayMidMs), tz);
        dayStarts.push(dStart);

        const dObj = new Date(dStart + 43200000);
        const weekday = dObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
        const dayMonth = dObj.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: tz });
        dayLabels.push(`${weekday} ${dayMonth}`);
      }

      const lastDayMidMs = firstDayStart + horizonDays * 86400000 + 43200000;
      const lastDayEnd = this.getLocalDayStartUtcMs(new Date(lastDayMidMs), tz);
      timestamps = [...dayStarts, lastDayEnd];
      labels = dayLabels;

      const dailySums: number[] = [];
      for (let d = 0; d < horizonDays; d++) {
        const dStart = dayStarts[d];
        const dEnd = timestamps[d + 1];
        const hoursInDay = Math.max(1, Math.round((dEnd - dStart) / 3600000));
        const dayHours: number[] = [];
        for (let h = 0; h < hoursInDay; h++) {
          dayHours.push(dStart + h * 3600000);
        }
        const hourlyVals = this.resampleSeries(rawOffsets, rawSeries, forecast.cycleEpoch, dayHours);
        let sum = 0;
        let hasVal = false;
        for (const v of hourlyVals) {
          if (v !== null && !isNaN(v)) {
            sum += Math.max(0, v);
            hasVal = true;
          }
        }
        dailySums.push(hasVal ? (isImperial ? Math.round(sum * 100) / 100 : Math.round(sum * 10) / 10) : (null as any));
      }

      let runningCum = 0;
      const cumSums: number[] = [];
      for (const dTot of dailySums) {
        runningCum += dTot;
        cumSums.push(isImperial ? Math.round(runningCum * 100) / 100 : Math.round(runningCum * 10) / 10);
      }

      barData = dailySums;
      cumData = cumSums;
      activeIdx = this.findActiveIndex(timestamps, state.currentTimeStep, true);
    }

    // Colors
    const normalBarBg = isSnow ? 'rgba(167, 139, 250, 0.65)' : 'rgba(96, 165, 250, 0.65)';
    const activeBarBg = isSnow ? 'rgba(147, 51, 234, 0.95)' : 'rgba(37, 99, 235, 0.95)';
    const normalBorder = isSnow ? 'rgba(167, 139, 250, 0.9)' : 'rgba(96, 165, 250, 0.9)';
    const activeBorder = isSnow ? '#e9d5ff' : '#bfdbfe';
    const cumLineColor = isSnow ? '#f472b6' : '#38bdf8';
    const cumFillColor = isSnow ? 'rgba(244, 114, 182, 0.12)' : 'rgba(56, 189, 248, 0.12)';

    const activeDayIdx = (isDaily && activeIdx !== -1) ? Math.min(horizonDays - 1, Math.max(0, Math.floor(activeIdx + 0.5))) : -1;

    const getBgColors = (actDay: number) => barData.map((_, i) => (isDaily && i === actDay ? activeBarBg : normalBarBg));
    const getBorderColors = (actDay: number) => barData.map((_, i) => (isDaily && i === actDay ? activeBorder : normalBorder));
    const getBorderWidths = (actDay: number) => barData.map((_, i) => (isDaily && i === actDay ? 1.5 : 1));

    const barDataset: any = {
      type: 'bar',
      label: horizonDays === 1
        ? (isSnow ? `Hourly Snow (${dataUnit})` : `Hourly Precip (${dataUnit})`)
        : (isSnow ? `Daily Snow (${dataUnit})` : `Daily Precip (${dataUnit})`),
      data: barData,
      backgroundColor: getBgColors(activeDayIdx),
      borderColor: getBorderColors(activeDayIdx),
      borderWidth: getBorderWidths(activeDayIdx),
      borderRadius: 3
    };

    barDataset._updateColors = (actDay: number) => {
      barDataset.backgroundColor = getBgColors(actDay);
      barDataset.borderColor = getBorderColors(actDay);
      barDataset.borderWidth = getBorderWidths(actDay);
    };

    const lineDataset: any = {
      type: 'line',
      label: `Cumulative (${dataUnit})`,
      data: cumData,
      borderColor: cumLineColor,
      backgroundColor: cumFillColor,
      borderWidth: 2,
      pointRadius: isDaily ? 3 : 0,
      pointHoverRadius: 5,
      pointHitRadius: 6,
      fill: true,
      tension: 0.3
    };

    const cursorPlugin = this.createCursorPlugin('precip', () => this.charts.get('precip')?.activeIdx ?? activeIdx);
    const timeTicksMap = !isDaily ? LegendTrendChartService.getTimeTickInfo(timestamps, tz, isImperial) : null;
    if (timeTicksMap) LegendTrendChartService.currentTimeTicksMap = timeTicksMap;
    const timeTicksPlugin = timeTicksMap ? LegendTrendChartService.createTimeTickMarksPlugin('precip', timeTicksMap) : null;

    const chartConfig: any = {
      data: {
        labels,
        datasets: [barDataset, lineDataset]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: { boxWidth: 8, font: { size: 8 }, color: '#cbd5e1' }
          },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const isCum = ctx.dataset.type === 'line';
                if (isCum) {
                  return ` Cumulative: ${ctx.parsed.y} ${dataUnit}`;
                }
                const prefix = horizonDays === 1 ? 'Hourly' : '24h Total';
                return ` ${prefix}: ${ctx.parsed.y} ${dataUnit}`;
              }
            }
          }
        },
        scales: {
          x: isDaily ? {
            grid: { display: false },
            ticks: {
              color: 'rgba(255, 255, 255, 0.7)',
              font: { size: 8 }
            }
          } : this.getCommonScales().x,
          y: {
            min: 0,
            suggestedMax: isImperial ? (isSnow ? 2 : 0.5) : (isSnow ? 5 : 10),
            grid: { color: 'rgba(255, 255, 255, 0.06)' },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 8 },
              callback: (val: any) => `${val} ${dataUnit}`
            }
          }
        }
      },
      plugins: timeTicksPlugin ? [timeTicksPlugin, cursorPlugin] : [cursorPlugin]
    };

    try {
      const existing = Chart.getChart(canvas);
      if (existing) {
        existing.destroy();
      }
    } catch (_e) {
      // ignore
    }

    const chart = new Chart(canvas, chartConfig);
    LegendTrendChartService.currentTimeTicksMap = null;

    if (this.pendingRequests.get('precip') !== reqId || !canvas.isConnected) {
      try {
        chart.destroy();
      } catch (_e) {
        // ignore
      }
      return;
    }

    if (loadingEl) loadingEl.style.display = 'none';
    canvas.style.display = 'block';

    this.charts.set('precip', {
      chart,
      poiKey,
      model,
      units,
      activeIdx,
      timestamps,
      horizonDays,
      startDayKey,
      optionsSubKey,
      isDaily,
      themeId,
      timeTicksMap: timeTicksMap || undefined
    });

    this.attachInteractionListeners(canvas, 'precip');
  }

  private static buildPointForecastChart(
    layerKey: string,
    canvas: HTMLCanvasElement,
    loadingEl: HTMLElement | null,
    forecast: DecodedPointForecast,
    poiKey: string,
    model: string,
    units: string,
    tz: string,
    state: AppState,
    optionsSubKey: string,
    horizonDays: number,
    startMs: number,
    startDayKey: string,
    reqId: number,
    themeId: string = 'dark'
  ): void {
    const isImperial = units === 'imperial';
    const rawOffsets = forecast.rawHourOffsets || getRawHourOffsetsForModel(model, forecast.numHours);
    const { timestamps } = this.buildHourlyGrid(startMs, horizonDays);
    const labels = this.formatLabels(timestamps, tz, isImperial);
    const timeTicksMap = LegendTrendChartService.getTimeTickInfo(timestamps, tz, isImperial);
    LegendTrendChartService.currentTimeTicksMap = timeTicksMap;

    let activeIdx = this.findActiveIndex(timestamps, state.currentTimeStep);
    const cursorPlugin = this.createCursorPlugin(layerKey, () => this.charts.get(layerKey)?.activeIdx ?? activeIdx);

    let chartConfig: any = null;

    if (layerKey === 'precip' && (optionsSubKey === '24h_rain' || optionsSubKey === '24h' || optionsSubKey === '24h_snow')) {
      this.buildDailyPrecipChart(canvas, loadingEl, forecast, poiKey, model, units, tz, state, optionsSubKey, horizonDays, startMs, startDayKey, reqId, themeId);
      return;
    }

    switch (layerKey) {
      case 'hrtemp': {
        const rawTemps = isImperial ? forecast.tempsF : forecast.tempsC;
        const temps = this.resampleSeries(rawOffsets, rawTemps, forecast.cycleEpoch, timestamps);
        const tempUnit = isImperial ? '°F' : '°C';
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: `${model.toUpperCase()} Temp`,
              data: temps,
              borderColor: '#f43f5e',
              backgroundColor: 'rgba(244, 63, 94, 0.12)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#f43f5e',
              fill: true,
              tension: 0.3,
              spanGaps: true
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Temp: ${ctx.parsed.y}${tempUnit}`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 9 },
                  callback: (val: any) => `${val}°`
                }
              }
            }
          }
        };
        break;
      }

      case 'lightning':
      case 'thunderstorm': {
        const hazards = this.resampleSeries(rawOffsets, forecast.lightningHazard, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
        const barColors = hazards.map(val => {
          if (val === null) return 'transparent';
          if (val < 10) return 'rgba(255, 255, 255, 0.05)';
          if (val < 35) return '#9664ff';
          if (val < 70) return '#ff8000';
          return '#ff0000';
        });
        const borderColors = hazards.map(val => (val !== null && val >= 10 ? '#facc15' : 'transparent'));

        chartConfig = {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'Lightning Hazard',
              data: hazards,
              backgroundColor: barColors,
              borderColor: borderColors,
              borderWidth: 1,
              borderRadius: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const v = Number(ctx.parsed.y);
                    const risk = v < 10 ? 'None' : v < 35 ? 'Low Risk' : v < 70 ? 'Moderate' : 'High Risk';
                    return ` Hazard: ${v}/100 (${risk})`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: 100,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  stepSize: 35,
                  color: '#94a3b8',
                  font: { size: 8 },
                  callback: (val: any) => {
                    const num = Number(val);
                    if (num === 0) return 'None';
                    if (num === 35) return 'Low';
                    if (num === 70) return 'Mod';
                    if (num === 100) return 'High';
                    return '';
                  }
                }
              }
            }
          }
        };
        break;
      }

      case 'thunderstorm': {
        const probs = this.resampleSeries(rawOffsets, forecast.lightningHazard, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
        const barColors = probs.map(val => {
          if (val === null) return 'transparent';
          if (val < 15) return 'rgba(255, 255, 255, 0.05)';
          if (val < 35) return '#00e5ff';
          if (val < 65) return '#facc15';
          return '#ef4444';
        });
        const borderColors = probs.map(val => (val !== null && val >= 15 ? (val >= 65 ? '#dc2626' : val >= 35 ? '#eab308' : '#38bdf8') : 'transparent'));

        chartConfig = {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'Thunderstorm Probability',
              data: probs,
              backgroundColor: barColors,
              borderColor: borderColors,
              borderWidth: 1,
              borderRadius: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const v = Number(ctx.parsed.y);
                    const cat = v < 15 ? 'None' : v < 35 ? 'Slight' : v < 65 ? 'Scattered' : 'Widespread/Severe';
                    return ` Prob: ${v}% (${cat})`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: 100,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  stepSize: 25,
                  color: '#94a3b8',
                  font: { size: 8 },
                  callback: (val: any) => `${val}%`
                }
              }
            }
          }
        };
        break;
      }

      case 'clouds': {
        const levels = state.cloudLevels || ['total'];
        const isTotalOnly = levels.includes('total');
        const datasets: any[] = [];

        // Check availability of total cover vs breakdown layers
        const hasTotalData = forecast.cloudCoverTotal && forecast.cloudCoverTotal.some(v => v > 0);
        const hasHighData = forecast.cloudCoverHigh && forecast.cloudCoverHigh.some(v => v > 0);
        const hasMidData = forecast.cloudCoverMid && forecast.cloudCoverMid.some(v => v > 0);
        const hasLowData = forecast.cloudCoverLow && forecast.cloudCoverLow.some(v => v > 0);
        const hasAnyBreakdown = hasHighData || hasMidData || hasLowData;

        if (isTotalOnly) {
          let rawTotal = forecast.cloudCoverTotal;
          // Graceful synthesis: if total cover is missing or all 0s, but altitude levels exist, derive total as max(high, mid, low)
          if (!hasTotalData && hasAnyBreakdown) {
            rawTotal = new Array(forecast.numHours);
            for (let i = 0; i < forecast.numHours; i++) {
              const h = forecast.cloudCoverHigh?.[i] || 0;
              const m = forecast.cloudCoverMid?.[i] || 0;
              const l = forecast.cloudCoverLow?.[i] || 0;
              rawTotal[i] = Math.max(h, m, l);
            }
          }
          const totalData = this.resampleSeries(rawOffsets, rawTotal, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
          datasets.push({
            label: 'Total Cloud Cover',
            data: totalData,
            borderColor: '#38bdf8',
            backgroundColor: 'rgba(56, 189, 248, 0.15)',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHitRadius: 6,
            pointBackgroundColor: '#38bdf8',
            fill: true,
            tension: 0.3,
            spanGaps: true
          });
        } else {
          // If breakdown mode requested, but binary tile only has total cover (no altitude tiers available)
          if (!hasAnyBreakdown && hasTotalData) {
            const totalData = this.resampleSeries(rawOffsets, forecast.cloudCoverTotal, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
            datasets.push({
              label: 'Total Cloud Cover (Breakdown N/A)',
              data: totalData,
              borderColor: '#38bdf8',
              backgroundColor: 'rgba(56, 189, 248, 0.15)',
              borderWidth: 2,
              borderDash: [3, 3],
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#38bdf8',
              fill: true,
              tension: 0.3,
              spanGaps: true
            });
          } else {
            if (levels.includes('high')) {
              const highData = this.resampleSeries(rawOffsets, forecast.cloudCoverHigh, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
              datasets.push({
                label: 'High (>20k ft)',
                data: highData,
                borderColor: '#d2d2d2',
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                tension: 0.2
              });
            }
            if (levels.includes('mid')) {
              const midData = this.resampleSeries(rawOffsets, forecast.cloudCoverMid, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
              datasets.push({
                label: 'Mid (6.5k-20k)',
                data: midData,
                borderColor: '#00b4b4',
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                tension: 0.2
              });
            }
            if (levels.includes('low')) {
              const lowData = this.resampleSeries(rawOffsets, forecast.cloudCoverLow, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
              datasets.push({
                label: 'Low (<6.5k ft)',
                data: lowData,
                borderColor: '#4682e6',
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                tension: 0.2
              });
            }
          }
        }

        chartConfig = {
          type: 'line',
          data: { labels, datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: {
                display: !isTotalOnly,
                position: 'top',
                labels: { boxWidth: 8, font: { size: 8 }, color: '#cbd5e1' }
              },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.parsed.y}%`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: 100,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val}%`
                }
              }
            }
          }
        };
        break;
      }

      case 'winds': {
        const speedUnit = isImperial ? 'mph' : 'm/s';
        const rawSpeeds = isImperial
          ? forecast.windSpeedMph
          : forecast.windSpeedMph.map(v => Math.round(v * 0.44704 * 10) / 10);
        const rawGusts = isImperial
          ? forecast.windGustMph
          : forecast.windGustMph.map(v => Math.round(v * 0.44704 * 10) / 10);

        const speeds = this.resampleSeries(rawOffsets, rawSpeeds, forecast.cycleEpoch, timestamps);
        const gusts = this.resampleSeries(rawOffsets, rawGusts, forecast.cycleEpoch, timestamps);

        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: 'Sustained Wind',
                data: speeds,
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                pointBackgroundColor: '#10b981',
                fill: true,
                tension: 0.3
              },
              {
                label: 'Wind Gust',
                data: gusts,
                borderColor: '#f59e0b',
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                borderDash: [2, 2],
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                pointBackgroundColor: '#f59e0b',
                fill: false,
                tension: 0.3
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: { boxWidth: 8, font: { size: 8 }, color: '#cbd5e1' }
              },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.parsed.y} ${speedUnit}`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val} ${speedUnit}`
                }
              }
            }
          }
        };
        break;
      }

      case 'precip': {
        const rateUnit = isImperial ? 'in/hr' : 'mm/h';
        const rawRates = isImperial
          ? forecast.precipRateMmH.map(v => Math.round((v / 25.4) * 100) / 100)
          : forecast.precipRateMmH;
        const rates = this.resampleSeries(rawOffsets, rawRates, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, v));
        const pops = this.resampleSeries(rawOffsets, forecast.precipProb, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
        const tstormProbs = this.resampleSeries(rawOffsets, forecast.lightningHazard, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));

        chartConfig = {
          data: {
            labels,
            datasets: [
              {
                type: 'bar',
                label: `Precip Rate (${rateUnit})`,
                data: rates,
                backgroundColor: rates.map(r => r === null ? 'transparent' : ((isImperial ? r >= 0.4 : r >= 10) ? '#9333ea' : '#60a5fa')),
                yAxisID: 'yRate',
                borderRadius: 2,
                order: 1
              },
              {
                type: 'bar',
                label: 'Thunderstorm Prob (%)',
                data: tstormProbs,
                backgroundColor: tstormProbs.map(p => {
                  if (p === null || p < 5) return 'transparent';
                  if (p < 25) return 'rgba(234, 179, 8, 0.4)';
                  if (p < 60) return 'rgba(249, 115, 22, 0.55)';
                  return 'rgba(239, 68, 68, 0.7)';
                }),
                borderColor: tstormProbs.map(p => {
                  if (p === null || p < 5) return 'transparent';
                  if (p < 25) return 'rgba(234, 179, 8, 0.8)';
                  if (p < 60) return 'rgba(249, 115, 22, 0.9)';
                  return '#ef4444';
                }),
                borderWidth: 1,
                borderRadius: 2,
                yAxisID: 'yThunderstorm',
                grouped: false,
                order: 2
              },
              {
                type: 'line',
                label: 'PoP (%)',
                data: pops,
                borderColor: '#38bdf8',
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                borderDash: [2, 2],
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                yAxisID: 'yPop',
                tension: 0.3,
                order: 0
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: { boxWidth: 8, font: { size: 8 }, color: '#cbd5e1' }
              },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    if (ctx.dataset.yAxisID === 'yPop') return ` PoP: ${ctx.parsed.y}%`;
                    if (ctx.dataset.yAxisID === 'yThunderstorm') {
                      const p = ctx.parsed.y;
                      const cat = p < 15 ? 'Low' : p < 35 ? 'Slight' : p < 65 ? 'Moderate' : 'High';
                      return ` Thunderstorm Prob: ${p}% (${cat})`;
                    }
                    return ` Rate: ${ctx.parsed.y} ${rateUnit}`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              yRate: {
                type: 'linear',
                position: 'left',
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 }
                }
              },
              yPop: {
                type: 'linear',
                position: 'right',
                min: 0,
                max: 100,
                grid: { display: false },
                ticks: {
                  color: '#38bdf8',
                  font: { size: 8 },
                  callback: (val: any) => `${val}%`
                }
              },
              yThunderstorm: {
                type: 'linear',
                display: false,
                min: 0,
                max: 220,
                grid: { display: false }
              }
            }
          }
        };
        break;
      }

      case 'relative_humidity': {
        const rhData = this.resampleSeries(rawOffsets, forecast.relativeHumidity, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Relative Humidity',
              data: rhData,
              borderColor: '#4dd0e1',
              backgroundColor: 'rgba(77, 208, 225, 0.15)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#4dd0e1',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` RH: ${ctx.parsed.y}%`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: 100,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val}%`
                }
              }
            }
          }
        };
        break;
      }

      case 'dewpoint': {
        const tempUnit = isImperial ? '°F' : '°C';
        const rawDew = isImperial
          ? (forecast.dewpointF || [])
          : (forecast.dewpointC || []);
        const dewData = this.resampleSeries(rawOffsets, rawDew, forecast.cycleEpoch, timestamps);

        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: `Dew Point (${tempUnit})`,
              data: dewData,
              borderColor: '#06b6d4',
              backgroundColor: 'rgba(6, 182, 212, 0.15)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#06b6d4',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Dew Point: ${ctx.parsed.y}${tempUnit}`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                ticks: {
                  color: '#94a3b8',
                  font: { size: 9 },
                  callback: (value: any) => `${value}${tempUnit}`
                },
                grid: { color: 'rgba(255, 255, 255, 0.05)' }
              }
            }
          }
        };
        break;
      }

      case 'optics_dew_risk': {
        const dewScores = this.resampleSeries(rawOffsets, forecast.dewRiskScore, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Optics Dew Risk',
              data: dewScores,
              borderColor: '#f59e0b',
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: dewScores.map(v => (v === null ? 'transparent' : v < 35 ? '#22c55e' : v < 70 ? '#f59e0b' : '#ef4444')),
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const v = Number(ctx.parsed.y);
                    const label = v < 35 ? 'Low Risk (Dry)' : v < 70 ? 'Moderate' : 'High Risk (Dewing)';
                    return ` Dew Risk: ${v}/100 (${label})`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: 100,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  stepSize: 35,
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => {
                    const num = Number(val);
                    if (num === 0) return 'Dry';
                    if (num === 35) return 'Low';
                    if (num === 70) return 'Mod';
                    if (num === 100) return 'Dew';
                    return '';
                  }
                }
              }
            }
          }
        };
        break;
      }

      case 'seeing': {
        const rawSeeing = forecast.seeingScore || [];
        const seeingScores = this.resampleSeries(rawOffsets, rawSeeing, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(100, Math.round(v))));
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Seeing Quality',
              data: seeingScores,
              borderColor: '#06b6d4',
              backgroundColor: 'rgba(6, 182, 212, 0.12)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: seeingScores.map(v => (v === null ? 'transparent' : v >= 80 ? '#2563eb' : v >= 60 ? '#06b6d4' : v >= 40 ? '#10b981' : v >= 20 ? '#f59e0b' : '#ef4444')),
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const v = Number(ctx.parsed.y);
                    const label = v >= 80 ? 'Exceptional (<1.0")' : v >= 60 ? 'Good (1.0-1.5")' : v >= 40 ? 'Average (1.5-2.0")' : v >= 20 ? 'Poor (2.0-2.5")' : 'Very Poor (>2.5")';
                    return ` Seeing: ${v}/100 (${label})`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: 100,
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.4)',
                  font: { size: 9 },
                  stepSize: 20,
                  callback: (val: any) => {
                    const num = Number(val);
                    if (num === 0) return 'Poor';
                    if (num === 50) return 'Avg';
                    if (num === 100) return 'Pris';
                    return '';
                  }
                }
              }
            }
          }
        };
        break;
      }

      case 'turbulence': {
        const rawEdr = forecast.turbulenceEdr || [];
        const edrVals = this.resampleSeries(rawOffsets, rawEdr, forecast.cycleEpoch, timestamps).map(v =>
          v === null ? null : Math.max(0, Math.min(1.0, Math.round(v * 100) / 100))
        );
        const validNums = edrVals.filter((v): v is number => v !== null && !isNaN(v));
        const peakEdr = validNums.length > 0 ? Math.max(...validNums) : 0.3;
        const yMax = Math.min(1.0, Math.max(0.5, Math.ceil((peakEdr + 0.08) * 10) / 10));
        const getEdrColor = (v: number | null): string => {
          if (v === null) return 'transparent';
          if (v >= 0.70) return '#d946ef'; // Extreme
          if (v >= 0.45) return '#ef4444'; // Severe
          if (v >= 0.20) return '#f59e0b'; // Moderate
          if (v >= 0.10) return '#84cc16'; // Light
          return '#2ecc71';                // Smooth
        };
        const getEdrTier = (v: number): string => {
          if (v >= 0.70) return 'Extreme';
          if (v >= 0.45) return 'Severe';
          if (v >= 0.20) return 'Moderate';
          if (v >= 0.10) return 'Light';
          return 'Smooth';
        };
        const lineColor = getEdrColor(peakEdr >= 0.20 ? peakEdr : 0.15);

        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Aviation Turbulence (EDR)',
              data: edrVals,
              borderColor: lineColor,
              backgroundColor: 'rgba(245, 158, 11, 0.14)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: edrVals.map(getEdrColor),
              segment: {
                borderColor: (ctx: any) => {
                  const y0 = ctx.p0?.parsed?.y ?? 0;
                  const y1 = ctx.p1?.parsed?.y ?? 0;
                  return getEdrColor(Math.max(y0, y1));
                }
              },
              fill: true,
              tension: 0.3,
              spanGaps: true
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const v = Number(ctx.parsed.y);
                    return ` Turbulence: ${v.toFixed(2)} EDR (${getEdrTier(v)})`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: yMax,
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.45)',
                  font: { size: 9 },
                  stepSize: 0.1,
                  callback: (val: any) => {
                    const num = Math.round(Number(val) * 100) / 100;
                    if (num === 0) return '0.00';
                    if (Math.abs(num - 0.2) < 0.01) return '0.20 Mod';
                    if (Math.abs(num - 0.4) < 0.01 || Math.abs(num - 0.5) < 0.01) return num.toFixed(2);
                    if (Math.abs(num - yMax) < 0.01) return num.toFixed(2);
                    return '';
                  }
                }
              }
            }
          }
        };
        break;
      }

      case 'freezing_level': {
        const altUnit = isImperial ? 'ft' : 'm';
        const rawFreezing = isImperial
          ? forecast.freezingLevelFt
          : forecast.freezingLevelFt.map(v => Math.round(v * 0.3048));
        const freezingVals = this.resampleSeries(rawOffsets, rawFreezing, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.round(v)));

        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: `Freezing Level (${altUnit})`,
              data: freezingVals,
              borderColor: '#8b5cf6',
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#8b5cf6',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Freezing Level: ${ctx.parsed.y.toLocaleString()} ${altUnit}`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val.toLocaleString()} ${altUnit}`
                }
              }
            }
          }
        };
        break;
      }

      case 'cloud_inversion': {
        const altUnit = isImperial ? 'ft' : 'm';
        const poi = state.selectedPoi;
        const poiElevFt = poi?.elev ?? 0;
        const targetElev = isImperial ? poiElevFt : Math.round(poiElevFt * 0.3048);

        const rawBase = forecast.cloudBaseFt || [];
        const rawTop = forecast.cloudTopFt || [];
        const rawTcc = forecast.cloudCoverTotal || [];

        if (rawBase.length === 0 && rawTop.length === 0) {
          this.showEmptyState(canvas, loadingEl, model);
          return;
        }

        const baseConverted = isImperial ? rawBase : rawBase.map(v => Math.round(v * 0.3048));
        const topConverted = isImperial ? rawTop : rawTop.map(v => Math.round(v * 0.3048));

        const baseVals = this.resampleSeries(rawOffsets, baseConverted, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.round(v)));
        const topVals = this.resampleSeries(rawOffsets, topConverted, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.round(v)));
        const tccVals = this.resampleSeries(rawOffsets, rawTcc, forecast.cycleEpoch, timestamps);
        const poiElevLine = new Array(timestamps.length).fill(targetElev);

        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: `Cloud Top (${altUnit})`,
                data: topVals,
                borderColor: 'rgba(56, 189, 248, 0.85)',
                backgroundColor: 'rgba(56, 189, 248, 0.25)',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                pointBackgroundColor: 'rgba(56, 189, 248, 1)',
                fill: false,
                tension: 0.3
              },
              {
                label: `Cloud Base (${altUnit})`,
                data: baseVals,
                borderColor: 'rgba(56, 189, 248, 0.45)',
                backgroundColor: 'rgba(56, 189, 248, 0.25)',
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                pointBackgroundColor: 'rgba(56, 189, 248, 0.8)',
                fill: '-1',
                tension: 0.3
              },
              {
                label: `POI Elevation (${targetElev.toLocaleString()} ${altUnit})`,
                data: poiElevLine,
                borderColor: '#f59e0b',
                borderWidth: 2,
                borderDash: [5, 4],
                pointRadius: 0,
                pointHoverRadius: 0,
                fill: false,
                tension: 0
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: {
                  color: 'rgba(255, 255, 255, 0.75)',
                  boxWidth: 10,
                  font: { size: 9 },
                  padding: 4
                }
              },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const idx = ctx.dataIndex;
                    const top = topVals[idx];
                    const base = baseVals[idx];
                    const tcc = tccVals[idx];
                    if (ctx.datasetIndex === 0) {
                      return ` Cloud Top: ${top !== null ? top.toLocaleString() : '--'} ${altUnit}`;
                    }
                    if (ctx.datasetIndex === 1) {
                      return ` Cloud Base: ${base !== null ? base.toLocaleString() : '--'} ${altUnit}`;
                    }
                    if (ctx.datasetIndex === 2) {
                      let status = 'Clear Skies';
                      if (top !== null && top > 0 && tcc !== null && tcc >= 10) {
                        if (targetElev > top) {
                          status = 'Above Clouds (Sea of Clouds Vista)';
                        } else if (base !== null && targetElev >= base) {
                          status = 'Submerged in Cloud Deck';
                        } else {
                          status = 'Below Cloud Ceiling';
                        }
                      }
                      return ` POI: ${targetElev.toLocaleString()} ${altUnit} [${status}]`;
                    }
                    return '';
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val.toLocaleString()} ${altUnit}`
                }
              }
            }
          }
        };
        break;
      }

      case 'cloud_base': {
        const altUnit = isImperial ? 'ft' : 'm';
        const poi = state.selectedPoi;
        const poiElevFt = poi?.elev ?? 0;
        const targetElev = isImperial ? poiElevFt : Math.round(poiElevFt * 0.3048);

        const rawBase = forecast.cloudBaseFt || [];
        if (rawBase.length === 0) {
          this.showEmptyState(canvas, loadingEl, model);
          return;
        }

        const baseConverted = isImperial ? rawBase : rawBase.map(v => Math.round(v * 0.3048));
        const baseVals = this.resampleSeries(rawOffsets, baseConverted, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.round(v)));
        const poiElevLine = new Array(timestamps.length).fill(targetElev);

        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: `Cloud Base (${altUnit})`,
                data: baseVals,
                borderColor: '#06b6d4',
                backgroundColor: 'rgba(6, 182, 212, 0.15)',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 6,
                pointBackgroundColor: '#06b6d4',
                fill: true,
                tension: 0.3
              },
              {
                label: `POI Ground (${targetElev.toLocaleString()} ${altUnit})`,
                data: poiElevLine,
                borderColor: '#f59e0b',
                borderWidth: 1.5,
                borderDash: [5, 4],
                pointRadius: 0,
                pointHoverRadius: 0,
                fill: false,
                tension: 0
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: {
                  color: 'rgba(255, 255, 255, 0.75)',
                  boxWidth: 10,
                  font: { size: 9 },
                  padding: 4
                }
              },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    if (ctx.datasetIndex === 0) {
                      const cb = ctx.parsed.y;
                      const agl = cb - targetElev;
                      const aglStr = agl > 0 ? `${agl.toLocaleString()} ${altUnit} AGL` : 'At/Below Surface (Fog)';
                      return ` Cloud Base: ${cb.toLocaleString()} ${altUnit} MSL (${aglStr})`;
                    }
                    return ` POI Elevation: ${targetElev.toLocaleString()} ${altUnit}`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val.toLocaleString()} ${altUnit}`
                }
              }
            }
          }
        };
        break;
      }

      case 'windchill': {
        const tempUnit = isImperial ? '°F' : '°C';
        const rawChills = isImperial
          ? forecast.windchillF
          : forecast.windchillF.map(v => Math.round((v - 32) / 1.8));
        const chills = this.resampleSeries(rawOffsets, rawChills, forecast.cycleEpoch, timestamps);

        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: `Wind Chill (${tempUnit})`,
              data: chills,
              borderColor: '#00f0ff',
              backgroundColor: 'rgba(0, 240, 255, 0.12)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#00f0ff',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Wind Chill: ${ctx.parsed.y}${tempUnit}`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val}°`
                }
              }
            }
          }
        };
        break;
      }

      case 'smoke': {
        const smokeVals = this.resampleSeries(rawOffsets, forecast.smokeSfc, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, v));
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Surface Smoke (µg/m³)',
              data: smokeVals,
              borderColor: '#ef4444',
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#ef4444',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Surface Smoke: ${ctx.parsed.y} µg/m³`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val} µg`
                }
              }
            }
          }
        };
        break;
      }

      case 'dust': {
        const dustVals = this.resampleSeries(rawOffsets, forecast.smokeSfc, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.round(v * 10) / 10));
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Surface Dust (µg/m³)',
              data: dustVals,
              borderColor: '#d97706',
              backgroundColor: 'rgba(217, 119, 6, 0.18)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#d97706',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Surface Dust: ${ctx.parsed.y} µg/m³`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val} µg`
                }
              }
            }
          }
        };
        break;
      }

      case 'aqi': {
        const useAqhi = (model === 'raqdps' || state.aqiMode === 'aqhi') &&
          Array.isArray(forecast.aqhiValues) &&
          forecast.aqhiValues.some(v => v > 0);

        if (useAqhi && forecast.aqhiValues) {
          const aqhiVals = this.resampleSeries(rawOffsets, forecast.aqhiValues, forecast.cycleEpoch, timestamps)
            .map(v => v === null ? null : Math.max(1, Math.round(v * 10) / 10));
          const getAqhiColor = (val: number | null) => {
            if (val === null) return 'transparent';
            if (val <= 3.5) return 'rgb(0, 180, 255)';
            if (val <= 6.5) return 'rgb(240, 210, 0)';
            if (val <= 10.5) return 'rgb(255, 130, 0)';
            return 'rgb(200, 0, 0)';
          };
          chartConfig = {
            type: 'bar',
            data: {
              labels,
              datasets: [{
                label: 'Canada ECCC AQHI',
                data: aqhiVals,
                backgroundColor: aqhiVals.map(getAqhiColor),
                borderRadius: 2
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              animation: { duration: 250 },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx: any) => {
                      const v = Number(ctx.parsed.y);
                      const cat =
                        v <= 3.5 ? 'Low Risk' :
                        v <= 6.5 ? 'Moderate Risk' :
                        v <= 10.5 ? 'High Risk' : 'Very High Risk';
                      return ` AQHI: ${v.toFixed(1)} (${cat})`;
                    }
                  }
                }
              },
              scales: {
                x: this.getCommonScales().x,
                y: {
                  min: 0,
                  suggestedMax: 10,
                  grid: { color: 'rgba(255, 255, 255, 0.06)' },
                  ticks: {
                    color: 'rgba(255, 255, 255, 0.6)',
                    font: { size: 8 }
                  }
                }
              }
            }
          };
          break;
        }

        const aqiVals = this.resampleSeries(rawOffsets, forecast.aqiValues, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.round(v)));
        const getAqiColor = (val: number | null) => {
          if (val === null) return 'transparent';
          if (val <= 50) return '#00e400';
          if (val <= 100) return '#ffff00';
          if (val <= 150) return '#ff7e00';
          if (val <= 200) return '#ff0000';
          if (val <= 300) return '#8f3f97';
          return '#7e0023';
        };

        chartConfig = {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'Air Quality Index',
              data: aqiVals,
              backgroundColor: aqiVals.map(getAqiColor),
              borderRadius: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const v = Number(ctx.parsed.y);
                    const cat =
                      v <= 50 ? 'Good' :
                      v <= 100 ? 'Moderate' :
                      v <= 150 ? 'Sensitive Groups' :
                      v <= 200 ? 'Unhealthy' :
                      v <= 300 ? 'Very Unhealthy' : 'Hazardous';
                    return ` AQI: ${v} (${cat})`;
                  }
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 }
                }
              }
            }
          }
        };
        break;
      }

      case 'darkskies': {
        const scores = this.resampleSeries(rawOffsets, forecast.darkskiesScore, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.min(10, Math.round(v * 10) / 10)));
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Dark Skies Score',
              data: scores,
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#6366f1',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Dark Skies Quality: ${ctx.parsed.y}/10`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                max: 10,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  stepSize: 2,
                  callback: (val: any) => `${val}`
                }
              }
            }
          }
        };
        break;
      }

      case 'sun_intensity': {
        const intensities = this.resampleSeries(rawOffsets, forecast.sunIntensityWm2, forecast.cycleEpoch, timestamps).map(v => v === null ? null : Math.max(0, Math.round(v)));
        chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Sun Intensity (W/m²)',
              data: intensities,
              borderColor: '#fb8c00',
              backgroundColor: 'rgba(251, 140, 0, 0.15)',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 6,
              pointBackgroundColor: '#fb8c00',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 250 },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` Sun Intensity: ${ctx.parsed.y} W/m²`
                }
              }
            },
            scales: {
              x: this.getCommonScales().x,
              y: {
                min: 0,
                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: { size: 8 },
                  callback: (val: any) => `${val} W`
                }
              }
            }
          }
        };
        break;
      }

      default:
        break;
    }

    if (!chartConfig) {
      LegendTrendChartService.currentTimeTicksMap = null;
      this.showEmptyState(canvas, loadingEl, model);
      return;
    }

    const refLinesPlugin = this.createReferenceLinesPlugin(layerKey);
    const timeTicksPlugin = LegendTrendChartService.createTimeTickMarksPlugin(layerKey, timeTicksMap);

    chartConfig.plugins = chartConfig.plugins || [];
    chartConfig.plugins.push(timeTicksPlugin);
    chartConfig.plugins.push(cursorPlugin);
    chartConfig.plugins.push(refLinesPlugin);

    try {
      const existing = Chart.getChart(canvas);
      if (existing) {
        existing.destroy();
      }
    } catch (_e) {
      // ignore
    }

    const chart = new Chart(canvas, chartConfig);
    LegendTrendChartService.currentTimeTicksMap = null;

    if (this.pendingRequests.get(layerKey) !== reqId || !canvas.isConnected) {
      try {
        chart.destroy();
      } catch (_e) {
        // ignore
      }
      return;
    }

    if (loadingEl) loadingEl.style.display = 'none';
    canvas.style.display = 'block';

    this.charts.set(layerKey, {
      chart,
      poiKey,
      model,
      units,
      activeIdx,
      timestamps,
      horizonDays,
      startDayKey,
      optionsSubKey,
      isDaily: false,
      themeId,
      refLinesEnabled: state.trendRefLinesEnabled !== false,
      timeTicksMap
    });

    this.attachInteractionListeners(canvas, layerKey);
  }
}
